import { z } from 'zod';
import { sha256 } from '@noble/hashes/sha256';

// src/chains/family.ts
var ChainFamily = {
  EVM: "evm",
  UTXO: "utxo",
  SOLANA: "solana",
  COSMOS: "cosmos",
  /** Hyperliquid's L1 (HyperCore): one signed action per transaction, EVM-shaped identifiers. */
  HYPERCORE: "hypercore",
  XRPL: "xrpl",
  TRON: "tron",
  NEAR: "near",
  OTHER: "other"
};
var EVM_IDENTIFIER_FAMILIES = /* @__PURE__ */ new Set([
  ChainFamily.EVM,
  ChainFamily.HYPERCORE
]);
function hasEvmIdentifiers(family) {
  return family != null && EVM_IDENTIFIER_FAMILIES.has(family);
}
function isChainFamily(value) {
  return typeof value === "string" && Object.values(ChainFamily).includes(value);
}
var EVM_DECODE_VOCABULARY = Object.freeze({
  schema: "ABI",
  call: "call",
  input: "calldata",
  log: "event log",
  target: "contract",
  targetLabel: "Contract",
  entrypoint: "function",
  interaction: "Contract call",
  schemaRegistry: true
});
var SOLANA_DECODE_VOCABULARY = Object.freeze({
  schema: "IDL",
  call: "instruction",
  input: "instruction data",
  log: "event",
  target: "program",
  targetLabel: "Program",
  entrypoint: "instruction",
  interaction: "Program instruction",
  schemaRegistry: false
});
var GENERIC_DECODE_VOCABULARY = Object.freeze({
  schema: "decoder",
  call: "call",
  input: "input",
  log: "event",
  target: "program",
  targetLabel: "Program",
  entrypoint: "call",
  interaction: "Protocol interaction",
  schemaRegistry: false
});
function decodeVocabulary(family) {
  if (family == null || family === ChainFamily.EVM) return EVM_DECODE_VOCABULARY;
  if (family === ChainFamily.SOLANA) return SOLANA_DECODE_VOCABULARY;
  return GENERIC_DECODE_VOCABULARY;
}

// src/singleton/index.ts
var NAMESPACE = "@authe/fe-commons/singleton/";
function globalSingleton(key, init) {
  const slot = /* @__PURE__ */ Symbol.for(NAMESPACE + key);
  const realm = globalThis;
  const existing = realm[slot];
  if (existing !== void 0) return existing;
  const created = init();
  realm[slot] = created;
  return created;
}
var BRAND_NAMESPACE = "@authe/fe-commons/brand/";
var ordinaryHasInstance = Function.prototype[Symbol.hasInstance];
function brandClass(cls, key) {
  const brand = /* @__PURE__ */ Symbol.for(BRAND_NAMESPACE + key);
  Object.defineProperty(cls.prototype, brand, { value: true });
  Object.defineProperty(cls, Symbol.hasInstance, {
    value(value) {
      if (this !== cls) return ordinaryHasInstance.call(this, value);
      return typeof value === "object" && value !== null && value[brand] === true;
    }
  });
}

// src/assets/naming.ts
var ASSET_FORMATS = ["svg", "webp", "png", "jpg", "gif", "avif"];
var ASSET_ROOT = "logos";
var CDN_ASSET_ROOT = "public/GET/cdn";
var PRIVATE_ASSET_ROOT = "assets/private";
var ALLOWED_ASSET_ROOTS = [ASSET_ROOT, CDN_ASSET_ROOT, PRIVATE_ASSET_ROOT];
var AssetNamingError = class extends Error {
  static {
    brandClass(this, "assets/AssetNamingError");
  }
  constructor(message) {
    super(message);
    this.name = "AssetNamingError";
  }
};
var MAX_SEGMENT = 80;
function isAssetFormat(value) {
  return typeof value === "string" && ASSET_FORMATS.includes(value);
}
function assetFormatFromExtension(pathOrExt) {
  const ext = pathOrExt.split(".").pop()?.toLowerCase().replace("jpeg", "jpg");
  return isAssetFormat(ext) ? ext : void 0;
}
function assertValidAssetKey(key) {
  if (!key || key.startsWith("/") || key.includes("\\") || key.includes("..") || key.includes("//")) {
    throw new AssetNamingError(`Invalid asset key "${key}"`);
  }
  if (!ALLOWED_ASSET_ROOTS.some((root) => key.startsWith(`${root}/`))) {
    const roots = ALLOWED_ASSET_ROOTS.map((r) => `"${r}/"`).join(" or ");
    throw new AssetNamingError(`Asset key "${key}" must start with ${roots}`);
  }
  if (!assetFormatFromExtension(key)) {
    throw new AssetNamingError(`Asset key "${key}" must end in one of: ${ASSET_FORMATS.join(", ")}`);
  }
  if (new TextEncoder().encode(key).length > 1024) {
    throw new AssetNamingError("Asset key exceeds S3's 1024-byte limit");
  }
  if (key.startsWith(`${ASSET_ROOT}/`)) {
    for (const segment of key.split("/")) {
      if (segment.length > MAX_SEGMENT) {
        throw new AssetNamingError(`Asset key segment "${segment}" exceeds ${MAX_SEGMENT} chars`);
      }
    }
  }
}
function assetUrl(key, opts) {
  assertValidAssetKey(key);
  const base = opts.cdnBase.replace(/\/+$/, "");
  return `${base}/${key.split("/").map(encodeURIComponent).join("/")}`;
}

// src/assets/public-cdn.ts
var publicCdn = globalSingleton("assets/public-cdn.v1", () => ({
  base: null
}));
function publicAssetUrl(key) {
  const base = publicCdn.base;
  const trimmed = key?.trim().replace(/^\/+/, "");
  if (!base || !trimmed) return null;
  try {
    return assetUrl(trimmed, { cdnBase: base });
  } catch (err) {
    if (err instanceof AssetNamingError) return null;
    throw err;
  }
}

// src/chains/model.ts
var ChainStatus = {
  /** Offered in pickers and processed. */
  ACTIVE: "active",
  /** The network is retired (Goerli, Mumbai …): rendered for history, never offered. */
  DEPRECATED: "deprecated",
  /** Switched off by an operator: rendered for history, hidden from pickers, not ingested. */
  DISABLED: "disabled"
};
var ChainLayer = { L1: "l1", L2: "l2", SIDECHAIN: "sidechain" };
var EXPLORER_LINK_TYPES = ["tx", "address", "block", "token"];
var ChainWireSchema = z.object({
  caip2: z.string().min(1),
  slug: z.string().min(1),
  chain_id_hex: z.string().nullable().default(null),
  family: z.string().refine(isChainFamily, { message: "unknown chain family" }),
  aliases: z.array(z.string()).default([]),
  name: z.string().min(1),
  short_name: z.string().nullable().default(null),
  native_token: z.string().min(1),
  native_name: z.string().nullable().default(null),
  // Required, like `reorg_depth`: Python always dumps it, and a default here
  // would be the FE guessing a chain's scale.
  decimals: z.number().int().min(0),
  slip44: z.number().int().min(0).nullable().default(null),
  native_asset: z.string().nullable().default(null),
  wrapped_native_address: z.string().nullable().default(null),
  status: z.enum(["active", "deprecated", "disabled"]).default("active"),
  is_testnet: z.boolean().default(false),
  mainnet: z.string().nullable().default(null),
  layer: z.enum(["l1", "l2", "sidechain"]).nullable().default(null),
  parent: z.string().nullable().default(null),
  // Required: rows arrive resolved (Python fills the mainnet's / family's depth).
  reorg_depth: z.number().int().min(0),
  block_time_seconds: z.number().positive().nullable().default(null),
  finality_blocks: z.number().int().positive().nullable().default(null),
  explorer_url: z.string().nullable().default(null),
  explorer_paths: z.record(z.string(), z.string().nullable()).default({}),
  rpc_urls: z.array(z.string()).default([]),
  providers: z.record(z.string(), z.string()).default({}),
  bech32_hrp: z.string().nullable().default(null),
  brand_color: z.string().nullable().default(null),
  logo: z.string().nullable().default(null),
  info_url: z.string().nullable().default(null),
  metadata: z.record(z.string(), z.unknown()).default({})
});
function foldAliases(aliases) {
  const seen = /* @__PURE__ */ new Set();
  for (const raw of aliases ?? []) {
    const alias = raw.trim().toLowerCase();
    if (alias) seen.add(alias);
  }
  return [...seen];
}
function httpsOrNull(url) {
  const trimmed = url?.trim();
  return trimmed && /^https:\/\/[^\s]+$/i.test(trimmed) ? trimmed : null;
}
function explorerPathsOf(raw) {
  const out = {};
  for (const type of EXPLORER_LINK_TYPES) if (type in raw) out[type] = raw[type] ?? null;
  return out;
}
function chainFromWire(row, mainnetSlugOf) {
  const wire = ChainWireSchema.parse(row);
  const logo = wire.logo?.trim() || null;
  return Object.freeze({
    caip2: wire.caip2,
    slug: wire.slug,
    chainIdHex: wire.chain_id_hex,
    family: wire.family,
    aliases: Object.freeze(foldAliases(wire.aliases)),
    name: wire.name,
    shortName: wire.short_name,
    nativeToken: wire.native_token,
    nativeName: wire.native_name,
    decimals: wire.decimals,
    slip44: wire.slip44,
    nativeAsset: wire.native_asset,
    wrappedNativeAddress: wire.wrapped_native_address,
    status: wire.status,
    isTestnet: wire.is_testnet,
    mainnet: wire.mainnet,
    mainnetSlug: wire.mainnet ? mainnetSlugOf?.(wire.mainnet) ?? null : null,
    layer: wire.layer,
    parent: wire.parent,
    reorgDepth: wire.reorg_depth,
    blockTimeSec: wire.block_time_seconds,
    finalityBlocks: wire.finality_blocks,
    explorerUrl: wire.explorer_url ? wire.explorer_url.replace(/\/+$/, "") : null,
    explorerPaths: Object.freeze(explorerPathsOf(wire.explorer_paths)),
    rpcUrls: Object.freeze([...wire.rpc_urls]),
    providers: Object.freeze({ ...wire.providers }),
    bech32Hrp: wire.bech32_hrp,
    brandColor: wire.brand_color,
    logo,
    // A getter, not a value: the bundled rows are parsed when the module loads,
    // before the app's bootstrap installs the CDN origin.
    get logoUrl() {
      return publicAssetUrl(logo);
    },
    infoUrl: httpsOrNull(wire.info_url),
    metadata: Object.freeze({ ...wire.metadata })
  });
}
var NATIVE_TOKEN = "native";
function isActiveChain(chain) {
  return chain.status === ChainStatus.ACTIVE;
}

// src/chains/catalogue.generated.json
var catalogue_generated_default = {
  chains: [
    {
      aliases: [
        "ape-chain",
        "apechain-mainnet"
      ],
      bech32_hrp: null,
      block_time_seconds: 0.9,
      brand_color: null,
      caip2: "eip155:33139",
      chain_id_hex: "0x8173",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://apescan.io",
      family: "evm",
      finality_blocks: null,
      info_url: "https://apechain.com",
      is_testnet: false,
      layer: "l2",
      logo: null,
      mainnet: null,
      metadata: {},
      name: "ApeChain",
      native_asset: null,
      native_name: "ApeCoin",
      native_token: "APE",
      parent: "eip155:42161",
      providers: {
        blockscout: "https://apechain.calderaexplorer.xyz/api",
        blockscout_v2: "https://apechain.calderaexplorer.xyz/api/v2",
        coingecko: "apechain",
        coingecko_native: "apecoin",
        defillama: "apechain",
        dexscreener: "apechain",
        evm_rpc: "discovery",
        sourcify: "33139"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc.apechain.com/http"
      ],
      short_name: "apechain",
      slip44: null,
      slug: "apechain",
      status: "active",
      wrapped_native_address: "0x48b62137edfa95a428d35c09e44256a739f6b557"
    },
    {
      aliases: [
        "curtis",
        "apechain-curtis",
        "apechain-testnet"
      ],
      bech32_hrp: null,
      block_time_seconds: 0.9,
      brand_color: null,
      caip2: "eip155:33111",
      chain_id_hex: "0x8157",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://explorer.curtis.apechain.com",
      family: "evm",
      finality_blocks: null,
      info_url: "https://curtis.hub.caldera.xyz",
      is_testnet: true,
      layer: "l2",
      logo: null,
      mainnet: "eip155:33139",
      metadata: {},
      name: "ApeChain Curtis",
      native_asset: null,
      native_name: "ApeCoin",
      native_token: "APE",
      parent: "eip155:421614",
      providers: {},
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc.curtis.apechain.com"
      ],
      short_name: "curtis",
      slip44: null,
      slug: "apechain_curtis",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "arbitrum-one",
        "arb",
        "arbitrum-mainnet"
      ],
      bech32_hrp: null,
      block_time_seconds: 0.25,
      brand_color: "#28A0F0",
      caip2: "eip155:42161",
      chain_id_hex: "0xa4b1",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://arbiscan.io",
      family: "evm",
      finality_blocks: 32,
      info_url: "https://arbitrum.io",
      is_testnet: false,
      layer: "l2",
      logo: "public/GET/cdn/blockchain/logos/svg/arbitrum.svg",
      mainnet: null,
      metadata: {},
      name: "Arbitrum",
      native_asset: "eip155:42161/slip44:60",
      native_name: "Ether",
      native_token: "ETH",
      parent: "eip155:1",
      providers: {
        alchemy: "arb-mainnet",
        ankr: "arbitrum",
        arkham: "arbitrum_one",
        blockchair: "arbitrum",
        blockscan: "42161",
        blockscout: "https://arbitrum.blockscout.com/api",
        blocksec: "42161",
        codeslaw: "arbitrum",
        coingecko: "arbitrum-one",
        coingecko_native: "ethereum",
        defillama: "arbitrum",
        dexscreener: "arbitrum",
        etherscan_scan: "arbiscan.io",
        infura: "arbitrum-mainnet",
        metasleuth: "arbitrum",
        moralis: "arbitrum",
        moralis_wallet: "arbitrum",
        sourcify: "42161"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://arb1.arbitrum.io/rpc",
        "https://arbitrum-one-rpc.publicnode.com"
      ],
      short_name: "arb1",
      slip44: 60,
      slug: "arbitrum",
      status: "active",
      wrapped_native_address: "0x82af49447d8a07e3bd95bd0d56f35241523fbab1"
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: 0.25,
      brand_color: "#28A0F0",
      caip2: "eip155:421614",
      chain_id_hex: "0x66eee",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://sepolia.arbiscan.io",
      family: "evm",
      finality_blocks: null,
      info_url: "https://arbitrum.io",
      is_testnet: true,
      layer: "l2",
      logo: "public/GET/cdn/blockchain/logos/svg/arbitrum.svg",
      mainnet: "eip155:42161",
      metadata: {},
      name: "Arbitrum Sepolia",
      native_asset: "eip155:421614/slip44:60",
      native_name: "Sepolia Ether",
      native_token: "ETH",
      parent: "eip155:11155111",
      providers: {
        alchemy: "arb-sepolia",
        etherscan_v2: "421614",
        infura: "arbitrum-sepolia",
        sourcify: "421614"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://sepolia-rollup.arbitrum.io/rpc",
        "https://arbitrum-sepolia-rpc.publicnode.com"
      ],
      short_name: "arb-sep",
      slip44: 60,
      slug: "arbitrum_sepolia",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: 1,
      brand_color: "#70D44B",
      caip2: "eip155:1313161554",
      chain_id_hex: "0x4e454152",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://explorer.aurora.dev",
      family: "evm",
      finality_blocks: null,
      info_url: "https://aurora.dev",
      is_testnet: false,
      layer: "sidechain",
      logo: null,
      mainnet: null,
      metadata: {},
      name: "Aurora",
      native_asset: "eip155:1313161554/slip44:60",
      native_name: "Ether",
      native_token: "ETH",
      parent: null,
      providers: {
        coingecko_native: "ethereum",
        defillama: "aurora",
        dexscreener: "aurora"
      },
      reorg_depth: 12,
      rpc_urls: [],
      short_name: "aurora",
      slip44: 60,
      slug: "aurora",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: 1,
      brand_color: "#70D44B",
      caip2: "eip155:1313161555",
      chain_id_hex: "0x4e454153",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://explorer.testnet.aurora.dev",
      family: "evm",
      finality_blocks: null,
      info_url: "https://aurora.dev",
      is_testnet: true,
      layer: "sidechain",
      logo: "public/GET/cdn/blockchain/logos/svg/aurora-testnet.svg",
      mainnet: "eip155:1313161554",
      metadata: {},
      name: "Aurora Testnet",
      native_asset: "eip155:1313161555/slip44:60",
      native_name: "Ether",
      native_token: "ETH",
      parent: null,
      providers: {},
      reorg_depth: 12,
      rpc_urls: [],
      short_name: "aurora-testnet",
      slip44: 60,
      slug: "aurora_testnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "avax",
        "avalanche-c-chain",
        "avax-c-chain",
        "avalanche-mainnet"
      ],
      bech32_hrp: null,
      block_time_seconds: 2,
      brand_color: "#E84142",
      caip2: "eip155:43114",
      chain_id_hex: "0xa86a",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://snowtrace.io",
      family: "evm",
      finality_blocks: null,
      info_url: "https://www.avax.network",
      is_testnet: false,
      layer: "l1",
      logo: "public/GET/cdn/blockchain/logos/svg/avalanche.svg",
      mainnet: null,
      metadata: {},
      name: "Avalanche",
      native_asset: "eip155:43114/slip44:9005",
      native_name: "Avalanche",
      native_token: "AVAX",
      parent: null,
      providers: {
        alchemy: "avax-mainnet",
        ankr: "avalanche",
        arkham: "avalanche",
        blockchair: "avalanche",
        blockscan: "43114",
        blocksec: "43114",
        coingecko: "avalanche",
        coingecko_native: "avalanche-2",
        defillama: "avax",
        dexscreener: "avalanche",
        etherscan_scan: "snowtrace.io",
        infura: "avalanche-mainnet",
        metasleuth: "avalanche",
        moralis: "avalanche",
        moralis_wallet: "avalanche",
        sourcify: "43114"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://api.avax.network/ext/bc/C/rpc",
        "https://avalanche-c-chain-rpc.publicnode.com"
      ],
      short_name: "avax",
      slip44: 9005,
      slug: "avalanche",
      status: "active",
      wrapped_native_address: "0xb31f66aa3c1e785363f0875a1b74e27b85fd66c7"
    },
    {
      aliases: [
        "fuji",
        "avalanche-fuji",
        "avax-fuji"
      ],
      bech32_hrp: null,
      block_time_seconds: 2,
      brand_color: "#E84142",
      caip2: "eip155:43113",
      chain_id_hex: "0xa869",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://testnet.snowtrace.io",
      family: "evm",
      finality_blocks: null,
      info_url: "https://cchain.explorer.avax-test.network",
      is_testnet: true,
      layer: "l1",
      logo: "public/GET/cdn/blockchain/logos/svg/avalanche.svg",
      mainnet: "eip155:43114",
      metadata: {},
      name: "Avalanche Fuji",
      native_asset: "eip155:43113/slip44:9005",
      native_name: "Avalanche",
      native_token: "AVAX",
      parent: null,
      providers: {
        alchemy: "avax-fuji",
        etherscan_v2: "43113",
        infura: "avalanche-fuji",
        sourcify: "43113"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://api.avax-test.network/ext/bc/C/rpc",
        "https://avalanche-fuji-c-chain-rpc.publicnode.com"
      ],
      short_name: "Fuji",
      slip44: 9005,
      slug: "avalanche_fuji",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "base-mainnet"
      ],
      bech32_hrp: null,
      block_time_seconds: 2,
      brand_color: "#0052FF",
      caip2: "eip155:8453",
      chain_id_hex: "0x2105",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://basescan.org",
      family: "evm",
      finality_blocks: null,
      info_url: "https://base.org",
      is_testnet: false,
      layer: "l2",
      logo: "public/GET/cdn/blockchain/logos/svg/base.svg",
      mainnet: null,
      metadata: {},
      name: "Base",
      native_asset: "eip155:8453/slip44:60",
      native_name: "Ether",
      native_token: "ETH",
      parent: "eip155:1",
      providers: {
        alchemy: "base-mainnet",
        ankr: "base",
        arkham: "base",
        blockchair: "base",
        blockscan: "8453",
        blockscout: "https://base.blockscout.com/api",
        codeslaw: "base",
        coingecko: "base",
        coingecko_native: "ethereum",
        defillama: "base",
        dexscreener: "base",
        etherscan_scan: "basescan.org",
        infura: "base-mainnet",
        metasleuth: "base",
        moralis: "base",
        moralis_wallet: "base",
        sourcify: "8453"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://mainnet.base.org",
        "https://base-rpc.publicnode.com"
      ],
      short_name: "base",
      slip44: 60,
      slug: "base",
      status: "active",
      wrapped_native_address: "0x4200000000000000000000000000000000000006"
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: 2,
      brand_color: "#0052FF",
      caip2: "eip155:84531",
      chain_id_hex: "0x14a33",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      info_url: "https://base.org",
      is_testnet: true,
      layer: "l2",
      logo: "public/GET/cdn/blockchain/logos/svg/base.svg",
      mainnet: "eip155:8453",
      metadata: {},
      name: "Base Goerli",
      native_asset: "eip155:84531/slip44:60",
      native_name: "Goerli Ether",
      native_token: "ETH",
      parent: "eip155:5",
      providers: {},
      reorg_depth: 12,
      rpc_urls: [],
      short_name: "basegor",
      slip44: 60,
      slug: "base_goerli",
      status: "deprecated",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: 2,
      brand_color: "#0052FF",
      caip2: "eip155:84532",
      chain_id_hex: "0x14a34",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://sepolia.basescan.org",
      family: "evm",
      finality_blocks: null,
      info_url: "https://base.org",
      is_testnet: true,
      layer: "l2",
      logo: "public/GET/cdn/blockchain/logos/svg/base-sepolia.svg",
      mainnet: "eip155:8453",
      metadata: {},
      name: "Base Sepolia",
      native_asset: "eip155:84532/slip44:60",
      native_name: "Sepolia Ether",
      native_token: "ETH",
      parent: "eip155:11155111",
      providers: {
        alchemy: "base-sepolia",
        infura: "base-sepolia",
        moralis: "base_sepolia",
        sourcify: "84532"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://sepolia.base.org",
        "https://base-sepolia-rpc.publicnode.com"
      ],
      short_name: "basesep",
      slip44: 60,
      slug: "base_sepolia",
      status: "active",
      wrapped_native_address: "0x4200000000000000000000000000000000000006"
    },
    {
      aliases: [
        "bitcoin-mainnet",
        "0x0",
        "btc"
      ],
      bech32_hrp: "bc",
      block_time_seconds: 600,
      brand_color: "#F7931A",
      caip2: "bip122:000000000019d6689c085ae165831e93",
      chain_id_hex: null,
      decimals: 8,
      explorer_paths: {},
      explorer_url: "https://blockstream.info",
      family: "utxo",
      finality_blocks: null,
      info_url: "https://bitcoin.org",
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {},
      name: "Bitcoin",
      native_asset: "bip122:000000000019d6689c085ae165831e93/slip44:0",
      native_name: "Bitcoin",
      native_token: "BTC",
      parent: null,
      providers: {
        blockchair: "bitcoin",
        coingecko: "ordinals",
        coingecko_native: "bitcoin",
        dexscreener: "bitcoin",
        esplora: "mainnet",
        scanscope: "bitcoin"
      },
      reorg_depth: 6,
      rpc_urls: [],
      short_name: null,
      slip44: 0,
      slug: "bitcoin",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "bitcoin-regtest"
      ],
      bech32_hrp: "bcrt",
      block_time_seconds: 600,
      brand_color: "#F7931A",
      caip2: "bip122:0f9188f13cb7b2c71f2a335e3a4fc328",
      chain_id_hex: null,
      decimals: 8,
      explorer_paths: {},
      explorer_url: null,
      family: "utxo",
      finality_blocks: null,
      info_url: null,
      is_testnet: true,
      layer: null,
      logo: null,
      mainnet: "bip122:000000000019d6689c085ae165831e93",
      metadata: {},
      name: "Bitcoin Regtest",
      native_asset: "bip122:0f9188f13cb7b2c71f2a335e3a4fc328/slip44:0",
      native_name: "Bitcoin",
      native_token: "BTC",
      parent: null,
      providers: {},
      reorg_depth: 1,
      rpc_urls: [],
      short_name: null,
      slip44: 0,
      slug: "bitcoin_regtest",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "bitcoin-testnet"
      ],
      bech32_hrp: "tb",
      block_time_seconds: 600,
      brand_color: "#F7931A",
      caip2: "bip122:000000000933ea01ad0ee984209779ba",
      chain_id_hex: null,
      decimals: 8,
      explorer_paths: {},
      explorer_url: null,
      family: "utxo",
      finality_blocks: null,
      info_url: null,
      is_testnet: true,
      layer: null,
      logo: null,
      mainnet: "bip122:000000000019d6689c085ae165831e93",
      metadata: {},
      name: "Bitcoin Testnet",
      native_asset: "bip122:000000000933ea01ad0ee984209779ba/slip44:0",
      native_name: "Bitcoin",
      native_token: "BTC",
      parent: null,
      providers: {
        esplora: "testnet"
      },
      reorg_depth: 6,
      rpc_urls: [],
      short_name: null,
      slip44: 0,
      slug: "bitcoin_testnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "blast-mainnet"
      ],
      bech32_hrp: null,
      block_time_seconds: 2,
      brand_color: "#FCFC03",
      caip2: "eip155:81457",
      chain_id_hex: "0x13e31",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://blastscan.io",
      family: "evm",
      finality_blocks: null,
      info_url: "https://blast.io",
      is_testnet: false,
      layer: "l2",
      logo: "public/GET/cdn/blockchain/logos/svg/blast.svg",
      mainnet: null,
      metadata: {},
      name: "Blast",
      native_asset: "eip155:81457/slip44:60",
      native_name: "Ether",
      native_token: "ETH",
      parent: "eip155:1",
      providers: {
        arkham: "blast",
        codeslaw: "blast",
        coingecko_native: "ethereum",
        defillama: "blast",
        dexscreener: "blast",
        sourcify: "81457"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://blast-rpc.publicnode.com"
      ],
      short_name: "blastmainnet",
      slip44: 60,
      slug: "blast",
      status: "active",
      wrapped_native_address: "0x4300000000000000000000000000000000000004"
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: 2,
      brand_color: "#FCFC03",
      caip2: "eip155:168587773",
      chain_id_hex: "0xa0c71fd",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://sepolia.blastscan.io",
      family: "evm",
      finality_blocks: null,
      info_url: "https://blast.io",
      is_testnet: true,
      layer: "l2",
      logo: "public/GET/cdn/blockchain/logos/svg/blast.svg",
      mainnet: "eip155:81457",
      metadata: {},
      name: "Blast Sepolia",
      native_asset: "eip155:168587773/slip44:60",
      native_name: "Sepolia Ether",
      native_token: "ETH",
      parent: "eip155:11155111",
      providers: {},
      reorg_depth: 12,
      rpc_urls: [],
      short_name: "blastsepolia",
      slip44: 60,
      slug: "blast_sepolia",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "binance",
        "binance-smart-chain",
        "bnb",
        "bnb-chain",
        "bnb-smart-chain"
      ],
      bech32_hrp: null,
      block_time_seconds: 3,
      brand_color: "#F0B90B",
      caip2: "eip155:56",
      chain_id_hex: "0x38",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://bscscan.com",
      family: "evm",
      finality_blocks: null,
      info_url: "https://www.bnbchain.org/en",
      is_testnet: false,
      layer: "l1",
      logo: "public/GET/cdn/blockchain/logos/svg/bsc.svg",
      mainnet: null,
      metadata: {},
      name: "BNB Chain",
      native_asset: "eip155:56/slip44:714",
      native_name: "BNB Chain Native Token",
      native_token: "BNB",
      parent: null,
      providers: {
        alchemy: "bnb-mainnet",
        ankr: "bsc",
        arkham: "bsc",
        blockchair: "binance-smart-chain",
        blockscan: "56",
        blocksec: "56",
        codeslaw: "bnbchain",
        coingecko: "binance-smart-chain",
        coingecko_native: "binancecoin",
        defillama: "bsc",
        dexscreener: "bsc",
        etherscan_scan: "bscscan.com",
        infura: "bsc-mainnet",
        metasleuth: "bsc",
        moralis: "bsc",
        moralis_wallet: "bsc",
        sourcify: "56"
      },
      reorg_depth: 15,
      rpc_urls: [
        "https://bsc-dataseed1.binance.org",
        "https://bsc-rpc.publicnode.com"
      ],
      short_name: "bnb",
      slip44: 714,
      slug: "bsc",
      status: "active",
      wrapped_native_address: "0xbb4cdb9cbd36b01bd1cbaebf2de08d9173bc095c"
    },
    {
      aliases: [
        "bnb-testnet"
      ],
      bech32_hrp: null,
      block_time_seconds: 3,
      brand_color: "#F0B90B",
      caip2: "eip155:97",
      chain_id_hex: "0x61",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://testnet.bscscan.com",
      family: "evm",
      finality_blocks: null,
      info_url: "https://www.bnbchain.org/en",
      is_testnet: true,
      layer: "l1",
      logo: "public/GET/cdn/blockchain/logos/svg/bsc-testnet.svg",
      mainnet: "eip155:56",
      metadata: {},
      name: "BSC Testnet",
      native_asset: "eip155:97/slip44:714",
      native_name: "BNB Chain Native Token",
      native_token: "tBNB",
      parent: null,
      providers: {
        blockchair: "bsc-testnet",
        infura: "bsc-testnet",
        moralis: "bsc_testnet"
      },
      reorg_depth: 12,
      rpc_urls: [],
      short_name: "bnbt",
      slip44: 714,
      slug: "bsc_testnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: 5,
      brand_color: "#FCFF52",
      caip2: "eip155:42220",
      chain_id_hex: "0xa4ec",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://celoscan.io",
      family: "evm",
      finality_blocks: null,
      info_url: "https://docs.celo.org",
      is_testnet: false,
      layer: "l1",
      logo: "public/GET/cdn/blockchain/logos/svg/celo.svg",
      mainnet: null,
      metadata: {},
      name: "Celo",
      native_asset: "eip155:42220/slip44:52752",
      native_name: "CELO",
      native_token: "CELO",
      parent: "eip155:1",
      providers: {
        coingecko_native: "celo",
        defillama: "celo",
        dexscreener: "celo"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://celo-rpc.publicnode.com"
      ],
      short_name: "celo",
      slip44: 52752,
      slug: "celo",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: 5,
      brand_color: "#FCFF52",
      caip2: "eip155:44787",
      chain_id_hex: "0xaef3",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      info_url: "https://docs.celo.org",
      is_testnet: true,
      layer: "l1",
      logo: "public/GET/cdn/blockchain/logos/svg/celo.svg",
      mainnet: "eip155:42220",
      metadata: {},
      name: "Celo Alfajores",
      native_asset: "eip155:44787/slip44:52752",
      native_name: "CELO",
      native_token: "CELO",
      parent: null,
      providers: {},
      reorg_depth: 12,
      rpc_urls: [],
      short_name: "ALFA",
      slip44: 52752,
      slug: "celo_alfajores",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:88888",
      chain_id_hex: "0x15b38",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      info_url: "https://chiliz.com",
      is_testnet: false,
      layer: null,
      logo: "public/GET/cdn/blockchain/logos/svg/chiliz.svg",
      mainnet: null,
      metadata: {},
      name: "Chiliz",
      native_asset: null,
      native_name: "Chiliz",
      native_token: "CHZ",
      parent: null,
      providers: {
        coingecko_native: "chiliz",
        moralis: "chiliz",
        moralis_wallet: "chiliz"
      },
      reorg_depth: 12,
      rpc_urls: [],
      short_name: "chzmainnet",
      slip44: null,
      slug: "chiliz",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:88882",
      chain_id_hex: "0x15b32",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      info_url: "https://www.chiliz.com/en/chain",
      is_testnet: true,
      layer: null,
      logo: "public/GET/cdn/blockchain/logos/svg/chiliz.svg",
      mainnet: "eip155:88888",
      metadata: {},
      name: "Chiliz Testnet",
      native_asset: null,
      native_name: "Chiliz",
      native_token: "CHZ",
      parent: null,
      providers: {},
      reorg_depth: 12,
      rpc_urls: [],
      short_name: "chzspicy",
      slip44: null,
      slug: "chiliz_spicy",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "cosmoshub-mainnet",
        "cosmoshub-4",
        "gaia"
      ],
      bech32_hrp: "cosmos",
      block_time_seconds: 5.5,
      brand_color: "#2E3148",
      caip2: "cosmos:cosmoshub-4",
      chain_id_hex: null,
      decimals: 6,
      explorer_paths: {
        token: null
      },
      explorer_url: "https://www.mintscan.io/cosmos",
      family: "cosmos",
      finality_blocks: null,
      info_url: "https://cosmos.network",
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {},
      name: "Cosmos Hub",
      native_asset: "cosmos:cosmoshub-4/slip44:118",
      native_name: "Atom",
      native_token: "ATOM",
      parent: null,
      providers: {
        coingecko: "cosmos",
        coingecko_native: "cosmos",
        cosmos_directory: "https://rest.cosmos.directory/cosmoshub",
        cosmos_lcd: "https://cosmos-rest.publicnode.com"
      },
      reorg_depth: 1,
      rpc_urls: [],
      short_name: null,
      slip44: 118,
      slug: "cosmoshub",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "cro"
      ],
      bech32_hrp: null,
      block_time_seconds: 6,
      brand_color: "#002D74",
      caip2: "eip155:25",
      chain_id_hex: "0x19",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://cronoscan.com",
      family: "evm",
      finality_blocks: null,
      info_url: "https://cronos.org",
      is_testnet: false,
      layer: "sidechain",
      logo: "public/GET/cdn/blockchain/logos/svg/cronos.svg",
      mainnet: null,
      metadata: {},
      name: "Cronos",
      native_asset: "eip155:25/slip44:394",
      native_name: "Cronos",
      native_token: "CRO",
      parent: null,
      providers: {
        arkham: "cronos",
        coingecko_native: "crypto-com-chain",
        defillama: "cronos",
        dexscreener: "cronos",
        moralis: "cronos",
        moralis_wallet: "cronos"
      },
      reorg_depth: 12,
      rpc_urls: [],
      short_name: "cro",
      slip44: 394,
      slug: "cronos",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: 13,
      brand_color: "#328332",
      caip2: "eip155:61",
      chain_id_hex: "0x3d",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://blockscout.com/etc/mainnet",
      family: "evm",
      finality_blocks: null,
      info_url: "https://ethereumclassic.org",
      is_testnet: false,
      layer: "l1",
      logo: null,
      mainnet: null,
      metadata: {
        note: "ETC shares pre-fork tx hashes with ETH. Audit sec. 2 calls out this collision risk."
      },
      name: "Ethereum Classic",
      native_asset: "eip155:61/slip44:61",
      native_name: "Ether",
      native_token: "ETC",
      parent: null,
      providers: {
        blockscout: "https://etc.blockscout.com/api",
        coingecko_native: "ethereum-classic"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://etc.etcdesktop.com"
      ],
      short_name: "etc",
      slip44: 61,
      slug: "etc",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "ethereum",
        "ethereum-mainnet",
        "eth-mainnet"
      ],
      bech32_hrp: null,
      block_time_seconds: 12,
      brand_color: "#627EEA",
      caip2: "eip155:1",
      chain_id_hex: "0x1",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://etherscan.io",
      family: "evm",
      finality_blocks: 96,
      info_url: "https://ethereum.org",
      is_testnet: false,
      layer: "l1",
      logo: "public/GET/cdn/blockchain/logos/svg/eth.svg",
      mainnet: null,
      metadata: {},
      name: "Ethereum",
      native_asset: "eip155:1/slip44:60",
      native_name: "Ether",
      native_token: "ETH",
      parent: null,
      providers: {
        alchemy: "eth-mainnet",
        ankr: "eth",
        arkham: "ethereum",
        blockchair: "ethereum",
        blockscan: "1",
        blockscout: "https://eth.blockscout.com/api",
        blocksec: "1",
        codeslaw: "ethereum",
        coingecko: "ethereum",
        coingecko_native: "ethereum",
        defillama: "ethereum",
        dexscreener: "ethereum",
        etherscan_scan: "etherscan.io",
        infura: "mainnet",
        metasleuth: "eth",
        moralis: "eth",
        moralis_wallet: "eth",
        scanscope: "eth",
        sourcify: "1"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://ethereum-rpc.publicnode.com",
        "https://eth.drpc.org",
        "https://mainnet.gateway.tenderly.co"
      ],
      short_name: "eth",
      slip44: 60,
      slug: "eth",
      status: "active",
      wrapped_native_address: "0xc02aaa39b223fe8d0a0e5c4f27ead9083c756cc2"
    },
    {
      aliases: [
        "ftm",
        "fantom-opera"
      ],
      bech32_hrp: null,
      block_time_seconds: 1,
      brand_color: "#1969FF",
      caip2: "eip155:250",
      chain_id_hex: "0xfa",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://ftmscan.com",
      family: "evm",
      finality_blocks: null,
      info_url: "https://fantom.foundation",
      is_testnet: false,
      layer: "l1",
      logo: "public/GET/cdn/blockchain/logos/svg/fantom.svg",
      mainnet: null,
      metadata: {},
      name: "Fantom",
      native_asset: "eip155:250/slip44:1007",
      native_name: "Fantom",
      native_token: "FTM",
      parent: null,
      providers: {
        blockchair: "fantom",
        blockscan: "250",
        blocksec: "250",
        coingecko: "fantom",
        coingecko_native: "fantom",
        defillama: "fantom",
        dexscreener: "fantom",
        etherscan_scan: "ftmscan.com",
        metasleuth: "fantom",
        sourcify: "250"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://rpcapi.fantom.network"
      ],
      short_name: "ftm",
      slip44: 1007,
      slug: "fantom",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: 1,
      brand_color: "#1969FF",
      caip2: "eip155:4002",
      chain_id_hex: "0xfa2",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      info_url: "https://docs.fantom.foundation/quick-start/short-guide#fantom-testnet",
      is_testnet: true,
      layer: "l1",
      logo: "public/GET/cdn/blockchain/logos/svg/fantom-testnet.svg",
      mainnet: "eip155:250",
      metadata: {},
      name: "Fantom Testnet",
      native_asset: "eip155:4002/slip44:1007",
      native_name: "Fantom",
      native_token: "FTM",
      parent: null,
      providers: {},
      reorg_depth: 12,
      rpc_urls: [],
      short_name: "tftm",
      slip44: 1007,
      slug: "fantom_testnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:747",
      chain_id_hex: "0x2eb",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://evm.flowscan.io",
      family: "evm",
      finality_blocks: null,
      info_url: "https://developers.flow.com/evm/about",
      is_testnet: false,
      layer: "l1",
      logo: "public/GET/cdn/blockchain/logos/svg/flow.svg",
      mainnet: null,
      metadata: {},
      name: "Flow",
      native_asset: "eip155:747/slip44:539",
      native_name: "FLOW",
      native_token: "FLOW",
      parent: null,
      providers: {
        coingecko_native: "flow",
        moralis: "flow",
        moralis_wallet: "flow"
      },
      reorg_depth: 12,
      rpc_urls: [],
      short_name: "flow-mainnet",
      slip44: 539,
      slug: "flow",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:545",
      chain_id_hex: "0x221",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://testnet.evm.flow.com",
      family: "evm",
      finality_blocks: null,
      info_url: "https://developers.flow.com/evm/about",
      is_testnet: true,
      layer: "l1",
      logo: "public/GET/cdn/blockchain/logos/svg/flow.svg",
      mainnet: "eip155:747",
      metadata: {},
      name: "Flow Testnet",
      native_asset: "eip155:545/slip44:539",
      native_name: "FLOW",
      native_token: "FLOW",
      parent: null,
      providers: {
        moralis: "flow_testnet"
      },
      reorg_depth: 12,
      rpc_urls: [],
      short_name: "flow-testnet",
      slip44: 539,
      slug: "flow_testnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: 5,
      brand_color: "#B4F9BA",
      caip2: "eip155:122",
      chain_id_hex: "0x7a",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      info_url: "https://fuse.io",
      is_testnet: false,
      layer: "l1",
      logo: null,
      mainnet: null,
      metadata: {},
      name: "Fuse",
      native_asset: null,
      native_name: "Fuse",
      native_token: "FUSE",
      parent: null,
      providers: {
        coingecko_native: "fuse-network-token"
      },
      reorg_depth: 12,
      rpc_urls: [],
      short_name: "fuse",
      slip44: null,
      slug: "fuse",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: 5,
      brand_color: "#B4F9BA",
      caip2: "eip155:123",
      chain_id_hex: "0x7b",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://explorer.fusespark.io",
      family: "evm",
      finality_blocks: null,
      info_url: "https://docs.fuse.io/general/fuse-network-blockchain/fuse-testnet",
      is_testnet: true,
      layer: "l1",
      logo: null,
      mainnet: "eip155:122",
      metadata: {},
      name: "Fuse Sparknet",
      native_asset: null,
      native_name: "Spark",
      native_token: "SPARK",
      parent: null,
      providers: {},
      reorg_depth: 12,
      rpc_urls: [],
      short_name: "spark",
      slip44: null,
      slug: "fuse_testnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "xdai",
        "gnosis-chain"
      ],
      bech32_hrp: null,
      block_time_seconds: 5,
      brand_color: "#0B6E4F",
      caip2: "eip155:100",
      chain_id_hex: "0x64",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://gnosisscan.io",
      family: "evm",
      finality_blocks: null,
      info_url: "https://docs.gnosischain.com",
      is_testnet: false,
      layer: "sidechain",
      logo: "public/GET/cdn/blockchain/logos/svg/gnosis.svg",
      mainnet: null,
      metadata: {},
      name: "Gnosis",
      native_asset: "eip155:100/slip44:700",
      native_name: "xDAI",
      native_token: "xDAI",
      parent: null,
      providers: {
        ankr: "gnosis",
        arkham: "gnosis",
        blockchair: "gnosis",
        blockscan: "100",
        coingecko_native: "xdai",
        defillama: "xdai",
        dexscreener: "gnosis",
        moralis: "gnosis",
        moralis_wallet: "gnosis"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc.gnosischain.com"
      ],
      short_name: "gno",
      slip44: 700,
      slug: "gnosis",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: 5,
      brand_color: "#0B6E4F",
      caip2: "eip155:10200",
      chain_id_hex: "0x27d8",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://gnosis-chiado.blockscout.com",
      family: "evm",
      finality_blocks: null,
      info_url: "https://docs.gnosischain.com",
      is_testnet: true,
      layer: "sidechain",
      logo: "public/GET/cdn/blockchain/logos/svg/gnosis-chiado.svg",
      mainnet: "eip155:100",
      metadata: {},
      name: "Gnosis Chiado",
      native_asset: "eip155:10200/slip44:700",
      native_name: "Chiado xDAI",
      native_token: "xDAI",
      parent: null,
      providers: {},
      reorg_depth: 12,
      rpc_urls: [],
      short_name: "chi",
      slip44: 700,
      slug: "gnosis_chiado",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: 12,
      brand_color: "#627EEA",
      caip2: "eip155:5",
      chain_id_hex: "0x5",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://goerli.etherscan.io",
      family: "evm",
      finality_blocks: null,
      info_url: "https://goerli.net/#about",
      is_testnet: true,
      layer: "l1",
      logo: "public/GET/cdn/blockchain/logos/svg/eth.svg",
      mainnet: "eip155:1",
      metadata: {},
      name: "Goerli",
      native_asset: "eip155:5/slip44:60",
      native_name: "Goerli Ether",
      native_token: "ETH",
      parent: null,
      providers: {},
      reorg_depth: 12,
      rpc_urls: [],
      short_name: "gor",
      slip44: 60,
      slug: "goerli",
      status: "deprecated",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:325",
      chain_id_hex: "0x145",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      info_url: "https://grvt.io",
      is_testnet: false,
      layer: "l2",
      logo: null,
      mainnet: null,
      metadata: {},
      name: "GRVT",
      native_asset: null,
      native_name: null,
      native_token: "GBT",
      parent: "eip155:1",
      providers: {},
      reorg_depth: 12,
      rpc_urls: [],
      short_name: "grvt",
      slip44: null,
      slug: "grvt",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: 2,
      brand_color: "#00AEE9",
      caip2: "eip155:1666600000",
      chain_id_hex: "0x63564c40",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      info_url: "https://www.harmony.one",
      is_testnet: false,
      layer: "l1",
      logo: null,
      mainnet: null,
      metadata: {},
      name: "Harmony",
      native_asset: "eip155:1666600000/slip44:1023",
      native_name: "ONE",
      native_token: "ONE",
      parent: null,
      providers: {
        coingecko_native: "harmony",
        defillama: "harmony",
        dexscreener: "harmony"
      },
      reorg_depth: 12,
      rpc_urls: [],
      short_name: "hmy-s0",
      slip44: 1023,
      slug: "harmony",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: 2,
      brand_color: "#00AEE9",
      caip2: "eip155:1666700000",
      chain_id_hex: "0x6357d2e0",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      info_url: "https://www.harmony.one",
      is_testnet: true,
      layer: "l1",
      logo: null,
      mainnet: "eip155:1666600000",
      metadata: {},
      name: "Harmony Testnet",
      native_asset: "eip155:1666700000/slip44:1023",
      native_name: "ONE",
      native_token: "ONE",
      parent: null,
      providers: {},
      reorg_depth: 12,
      rpc_urls: [],
      short_name: "hmy-b-s0",
      slip44: 1023,
      slug: "harmony_testnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: 12,
      brand_color: "#627EEA",
      caip2: "eip155:17000",
      chain_id_hex: "0x4268",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      info_url: "https://holesky.ethpandaops.io",
      is_testnet: true,
      layer: "l1",
      logo: "public/GET/cdn/blockchain/logos/svg/eth.svg",
      mainnet: "eip155:1",
      metadata: {},
      name: "Holesky",
      native_asset: "eip155:17000/slip44:60",
      native_name: "Testnet ETH",
      native_token: "ETH",
      parent: null,
      providers: {},
      reorg_depth: 12,
      rpc_urls: [],
      short_name: "holesky",
      slip44: 60,
      slug: "holesky",
      status: "deprecated",
      wrapped_native_address: null
    },
    {
      aliases: [
        "hypercore-mainnet",
        "hyperliquid",
        "hyperliquid-core",
        "hyperliquid-l1"
      ],
      bech32_hrp: null,
      block_time_seconds: 0.2,
      brand_color: "#97FCE4",
      caip2: "hyperliq:mainnet",
      chain_id_hex: null,
      decimals: 8,
      explorer_paths: {
        token: null
      },
      explorer_url: "https://app.hyperliquid.xyz/explorer",
      family: "hypercore",
      finality_blocks: null,
      info_url: "https://hyperliquid.xyz",
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {},
      name: "HyperCore",
      native_asset: "hyperliq:mainnet/slip44:2457",
      native_name: "Hyperliquid",
      native_token: "HYPE",
      parent: null,
      providers: {
        coingecko_native: "hyperliquid",
        hyperliquid: "mainnet"
      },
      reorg_depth: 1,
      rpc_urls: [],
      short_name: null,
      slip44: 2457,
      slug: "hypercore",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "hypercore-testnet",
        "hyperliquid-testnet",
        "hypercore_testnet"
      ],
      bech32_hrp: null,
      block_time_seconds: 0.2,
      brand_color: "#97FCE4",
      caip2: "hyperliq:testnet",
      chain_id_hex: null,
      decimals: 8,
      explorer_paths: {
        token: null
      },
      explorer_url: "https://app.hyperliquid-testnet.xyz/explorer",
      family: "hypercore",
      finality_blocks: null,
      info_url: null,
      is_testnet: true,
      layer: null,
      logo: null,
      mainnet: "hyperliq:mainnet",
      metadata: {},
      name: "HyperCore Testnet",
      native_asset: "hyperliq:testnet/slip44:2457",
      native_name: "Hyperliquid",
      native_token: "HYPE",
      parent: null,
      providers: {
        hyperliquid: "testnet"
      },
      reorg_depth: 1,
      rpc_urls: [],
      short_name: null,
      slip44: 2457,
      slug: "hypercore_testnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "hyper-evm",
        "hyperliquid-evm"
      ],
      bech32_hrp: null,
      block_time_seconds: 1,
      brand_color: "#97FCE4",
      caip2: "eip155:999",
      chain_id_hex: "0x3e7",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://hyperevmscan.io",
      family: "evm",
      finality_blocks: null,
      info_url: "https://hyperliquid.xyz",
      is_testnet: false,
      layer: "l1",
      logo: "public/GET/cdn/blockchain/logos/svg/hyperevm.svg",
      mainnet: null,
      metadata: {},
      name: "HyperEVM",
      native_asset: "eip155:999/slip44:2457",
      native_name: "HYPE",
      native_token: "HYPE",
      parent: null,
      providers: {
        coingecko_native: "hyperliquid",
        evm_rpc: "ingest",
        sourcify: "999"
      },
      reorg_depth: 1,
      rpc_urls: [
        "https://rpc.hyperliquid.xyz/evm"
      ],
      short_name: null,
      slip44: 2457,
      slug: "hyperevm",
      status: "active",
      wrapped_native_address: "0x5555555555555555555555555555555555555555"
    },
    {
      aliases: [
        "hyperevm-testnet",
        "hyperliquid-evm-testnet"
      ],
      bech32_hrp: null,
      block_time_seconds: 1,
      brand_color: "#97FCE4",
      caip2: "eip155:998",
      chain_id_hex: "0x3e6",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      info_url: "https://hyperfoundation.org",
      is_testnet: true,
      layer: "l1",
      logo: "public/GET/cdn/blockchain/logos/svg/hyperevm.svg",
      mainnet: "eip155:999",
      metadata: {},
      name: "HyperEVM Testnet",
      native_asset: "eip155:998/slip44:2457",
      native_name: "HYPE",
      native_token: "HYPE",
      parent: null,
      providers: {
        evm_rpc: "ingest",
        sourcify: "998"
      },
      reorg_depth: 1,
      rpc_urls: [
        "https://rpc.hyperliquid-testnet.xyz/evm"
      ],
      short_name: "hype-evm-testnet",
      slip44: 2457,
      slug: "hyperevm_testnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:57073",
      chain_id_hex: "0xdef1",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://explorer.inkonchain.com",
      family: "evm",
      finality_blocks: null,
      info_url: "https://inkonchain.com",
      is_testnet: false,
      layer: "l2",
      logo: "public/GET/cdn/blockchain/logos/svg/ink.svg",
      mainnet: null,
      metadata: {},
      name: "Ink",
      native_asset: "eip155:57073/slip44:60",
      native_name: "Ether",
      native_token: "ETH",
      parent: "eip155:1",
      providers: {
        blockscout_v2: "https://explorer.inkonchain.com/api/v2",
        coingecko_native: "ethereum",
        sourcify: "57073"
      },
      reorg_depth: 12,
      rpc_urls: [],
      short_name: "ink",
      slip44: 60,
      slug: "ink",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: "#FF433E",
      caip2: "eip155:2222",
      chain_id_hex: "0x8ae",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      info_url: "https://www.kava.io",
      is_testnet: false,
      layer: "l1",
      logo: null,
      mainnet: null,
      metadata: {},
      name: "Kava",
      native_asset: "eip155:2222/slip44:459",
      native_name: "Kava",
      native_token: "KAVA",
      parent: null,
      providers: {
        coingecko_native: "kava",
        defillama: "kava"
      },
      reorg_depth: 12,
      rpc_urls: [],
      short_name: "kava",
      slip44: 459,
      slug: "kava",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: "#FF433E",
      caip2: "eip155:2221",
      chain_id_hex: "0x8ad",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      info_url: "https://www.kava.io",
      is_testnet: true,
      layer: "l1",
      logo: null,
      mainnet: "eip155:2222",
      metadata: {},
      name: "Kava Testnet",
      native_asset: "eip155:2221/slip44:459",
      native_name: "TKava",
      native_token: "TKAVA",
      parent: null,
      providers: {},
      reorg_depth: 12,
      rpc_urls: [],
      short_name: "tkava",
      slip44: 459,
      slug: "kava_testnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: 1,
      brand_color: "#FF1B1B",
      caip2: "eip155:8217",
      chain_id_hex: "0x2019",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      info_url: "https://kaia.io",
      is_testnet: false,
      layer: "l1",
      logo: "public/GET/cdn/blockchain/logos/svg/klaytn.svg",
      mainnet: null,
      metadata: {},
      name: "Klaytn",
      native_asset: "eip155:8217/slip44:8217",
      native_name: "KAIA",
      native_token: "KAIA",
      parent: null,
      providers: {
        coingecko_native: "kaia",
        defillama: "klaytn"
      },
      reorg_depth: 12,
      rpc_urls: [],
      short_name: "kaia-mainnet",
      slip44: 8217,
      slug: "klaytn",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: 1,
      brand_color: "#FF1B1B",
      caip2: "eip155:1001",
      chain_id_hex: "0x3e9",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      info_url: "https://kaia.io",
      is_testnet: true,
      layer: "l1",
      logo: "public/GET/cdn/blockchain/logos/svg/klaytn-baobab.svg",
      mainnet: "eip155:8217",
      metadata: {},
      name: "Klaytn Baobab",
      native_asset: "eip155:1001/slip44:8217",
      native_name: "KAIA",
      native_token: "KAIA",
      parent: null,
      providers: {},
      reorg_depth: 12,
      rpc_urls: [],
      short_name: "kaia-kairos",
      slip44: 8217,
      slug: "klaytn_baobab",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "linea-mainnet"
      ],
      bech32_hrp: null,
      block_time_seconds: 3,
      brand_color: "#61DFFF",
      caip2: "eip155:59144",
      chain_id_hex: "0xe708",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://lineascan.build",
      family: "evm",
      finality_blocks: null,
      info_url: "https://linea.build",
      is_testnet: false,
      layer: "l2",
      logo: "public/GET/cdn/blockchain/logos/svg/linea.svg",
      mainnet: null,
      metadata: {},
      name: "Linea",
      native_asset: "eip155:59144/slip44:60",
      native_name: "Linea Ether",
      native_token: "ETH",
      parent: "eip155:1",
      providers: {
        arkham: "linea",
        coingecko_native: "ethereum",
        defillama: "linea",
        dexscreener: "linea",
        etherscan_scan: "lineascan.build",
        moralis: "linea",
        moralis_wallet: "linea",
        sourcify: "59144"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc.linea.build"
      ],
      short_name: "linea",
      slip44: 60,
      slug: "linea",
      status: "active",
      wrapped_native_address: "0xe5d7c2a44ffddf6b295a15c148167daaaf5cf34f"
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: 3,
      brand_color: "#61DFFF",
      caip2: "eip155:59141",
      chain_id_hex: "0xe705",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://sepolia.lineascan.build",
      family: "evm",
      finality_blocks: null,
      info_url: "https://linea.build",
      is_testnet: true,
      layer: "l2",
      logo: "public/GET/cdn/blockchain/logos/svg/linea-sepolia.svg",
      mainnet: "eip155:59144",
      metadata: {},
      name: "Linea Sepolia",
      native_asset: "eip155:59141/slip44:60",
      native_name: "Linea Ether",
      native_token: "ETH",
      parent: "eip155:11155111",
      providers: {},
      reorg_depth: 12,
      rpc_urls: [],
      short_name: "linea-sepolia",
      slip44: 60,
      slug: "linea_sepolia",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: 3,
      brand_color: "#61DFFF",
      caip2: "eip155:59140",
      chain_id_hex: "0xe704",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      info_url: "https://linea.build",
      is_testnet: true,
      layer: "l2",
      logo: "public/GET/cdn/blockchain/logos/svg/linea.svg",
      mainnet: "eip155:59144",
      metadata: {},
      name: "Linea Goerli",
      native_asset: "eip155:59140/slip44:60",
      native_name: "Linea Ether",
      native_token: "ETH",
      parent: "eip155:5",
      providers: {},
      reorg_depth: 12,
      rpc_urls: [],
      short_name: "linea-goerli",
      slip44: 60,
      slug: "linea_testnet",
      status: "deprecated",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:1135",
      chain_id_hex: "0x46f",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://blockscout.lisk.com",
      family: "evm",
      finality_blocks: null,
      info_url: "https://lisk.com",
      is_testnet: false,
      layer: "l2",
      logo: "public/GET/cdn/blockchain/logos/svg/lisk.svg",
      mainnet: null,
      metadata: {},
      name: "Lisk",
      native_asset: "eip155:1135/slip44:60",
      native_name: "Ether",
      native_token: "ETH",
      parent: "eip155:1",
      providers: {
        coingecko_native: "lisk",
        moralis: "lisk"
      },
      reorg_depth: 12,
      rpc_urls: [],
      short_name: "lisk",
      slip44: 60,
      slug: "lisk",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:4202",
      chain_id_hex: "0x106a",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      info_url: "https://lisk.com",
      is_testnet: true,
      layer: "l2",
      logo: "public/GET/cdn/blockchain/logos/svg/lisk.svg",
      mainnet: "eip155:1135",
      metadata: {},
      name: "Lisk Sepolia",
      native_asset: "eip155:4202/slip44:60",
      native_name: "Sepolia Ether",
      native_token: "ETH",
      parent: "eip155:11155111",
      providers: {},
      reorg_depth: 12,
      rpc_urls: [],
      short_name: "lisksep",
      slip44: 60,
      slug: "lisk_sepolia",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: 2,
      brand_color: "#000000",
      caip2: "eip155:5000",
      chain_id_hex: "0x1388",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://explorer.mantle.xyz",
      family: "evm",
      finality_blocks: null,
      info_url: "https://mantle.xyz",
      is_testnet: false,
      layer: "l2",
      logo: "public/GET/cdn/blockchain/logos/svg/mantle.svg",
      mainnet: null,
      metadata: {},
      name: "Mantle",
      native_asset: null,
      native_name: "Mantle",
      native_token: "MNT",
      parent: "eip155:1",
      providers: {
        arkham: "mantle",
        coingecko_native: "mantle",
        defillama: "mantle",
        dexscreener: "mantle"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc.mantle.xyz"
      ],
      short_name: "mantle",
      slip44: null,
      slug: "mantle",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "mantle-testnet",
        "mantle_testnet"
      ],
      bech32_hrp: null,
      block_time_seconds: 2,
      brand_color: "#000000",
      caip2: "eip155:5003",
      chain_id_hex: "0x138b",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://sepolia.mantlescan.xyz",
      family: "evm",
      finality_blocks: null,
      info_url: "https://mantle.xyz",
      is_testnet: true,
      layer: "l2",
      logo: "public/GET/cdn/blockchain/logos/svg/mantle-sepolia.svg",
      mainnet: "eip155:5000",
      metadata: {},
      name: "Mantle Sepolia",
      native_asset: null,
      native_name: "Sepolia Mantle",
      native_token: "MNT",
      parent: "eip155:11155111",
      providers: {},
      reorg_depth: 12,
      rpc_urls: [],
      short_name: "mnt-sep",
      slip44: null,
      slug: "mantle_sepolia",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: 4,
      brand_color: "#00DACC",
      caip2: "eip155:1088",
      chain_id_hex: "0x440",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      info_url: "https://www.metis.io",
      is_testnet: false,
      layer: "l2",
      logo: "public/GET/cdn/blockchain/logos/svg/metis.svg",
      mainnet: null,
      metadata: {},
      name: "Metis",
      native_asset: null,
      native_name: "Metis",
      native_token: "METIS",
      parent: "eip155:1",
      providers: {
        coingecko_native: "metis-token",
        defillama: "metis"
      },
      reorg_depth: 12,
      rpc_urls: [],
      short_name: "metis-andromeda",
      slip44: null,
      slug: "metis",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: 4,
      brand_color: "#00DACC",
      caip2: "eip155:588",
      chain_id_hex: "0x24c",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      info_url: "https://www.metis.io",
      is_testnet: true,
      layer: "l2",
      logo: "public/GET/cdn/blockchain/logos/svg/metis.svg",
      mainnet: "eip155:1088",
      metadata: {},
      name: "Metis Goerli",
      native_asset: null,
      native_name: "tMetis",
      native_token: "METIS",
      parent: "eip155:4",
      providers: {},
      reorg_depth: 12,
      rpc_urls: [],
      short_name: "metis-stardust",
      slip44: null,
      slug: "metis_goerli",
      status: "deprecated",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:143",
      chain_id_hex: "0x8f",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://monadexplorer.com",
      family: "evm",
      finality_blocks: null,
      info_url: "https://monad.xyz",
      is_testnet: false,
      layer: "l1",
      logo: "public/GET/cdn/blockchain/logos/svg/monad.svg",
      mainnet: null,
      metadata: {},
      name: "Monad",
      native_asset: "eip155:143/slip44:268435779",
      native_name: "Monad",
      native_token: "MON",
      parent: null,
      providers: {
        coingecko_native: "monad",
        moralis: "monad",
        moralis_wallet: "monad"
      },
      reorg_depth: 12,
      rpc_urls: [],
      short_name: "mon",
      slip44: 268435779,
      slug: "monad",
      status: "active",
      wrapped_native_address: "0x3bd359c1119da7da1d913d1c4d2b7c461115433a"
    },
    {
      aliases: [
        "monero-mainnet",
        "xmr"
      ],
      bech32_hrp: null,
      block_time_seconds: 120,
      brand_color: "#FF6600",
      caip2: "monero:418015bb9ae982a1975da7d79277c270",
      chain_id_hex: null,
      decimals: 12,
      explorer_paths: {
        address: null,
        token: null
      },
      explorer_url: "https://xmrchain.net",
      family: "utxo",
      finality_blocks: null,
      info_url: "https://www.getmonero.org",
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {},
      name: "Monero",
      native_asset: "monero:418015bb9ae982a1975da7d79277c270/slip44:128",
      native_name: "Monero",
      native_token: "XMR",
      parent: null,
      providers: {
        blockchair: "monero",
        blockchair_api: "monero",
        coingecko_native: "monero",
        xmrchain: "mainnet"
      },
      reorg_depth: 10,
      rpc_urls: [],
      short_name: null,
      slip44: 128,
      slug: "monero",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: 12,
      brand_color: "#53CBC8",
      caip2: "eip155:1287",
      chain_id_hex: "0x507",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://moonbase.moonscan.io",
      family: "evm",
      finality_blocks: null,
      info_url: "https://docs.moonbeam.network/learn/platform/networks/moonbase",
      is_testnet: true,
      layer: "l1",
      logo: "public/GET/cdn/blockchain/logos/svg/moonbase.svg",
      mainnet: "eip155:1284",
      metadata: {},
      name: "Moonbase",
      native_asset: "eip155:1287/slip44:1284",
      native_name: "Dev",
      native_token: "DEV",
      parent: null,
      providers: {},
      reorg_depth: 12,
      rpc_urls: [],
      short_name: "mbase",
      slip44: 1284,
      slug: "moonbase",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: 12,
      brand_color: "#53CBC8",
      caip2: "eip155:1284",
      chain_id_hex: "0x504",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://moonscan.io",
      family: "evm",
      finality_blocks: null,
      info_url: "https://moonbeam.network/networks/moonbeam",
      is_testnet: false,
      layer: "l1",
      logo: "public/GET/cdn/blockchain/logos/svg/moonbeam.svg",
      mainnet: null,
      metadata: {},
      name: "Moonbeam",
      native_asset: "eip155:1284/slip44:1284",
      native_name: "Glimmer",
      native_token: "GLMR",
      parent: null,
      providers: {
        coingecko_native: "moonbeam",
        defillama: "moonbeam",
        dexscreener: "moonbeam",
        metasleuth: "moonbeam",
        moralis: "moonbeam"
      },
      reorg_depth: 12,
      rpc_urls: [],
      short_name: "mbeam",
      slip44: 1284,
      slug: "moonbeam",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: 12,
      brand_color: "#C0C0C0",
      caip2: "eip155:1285",
      chain_id_hex: "0x505",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      info_url: "https://moonbeam.network/networks/moonriver",
      is_testnet: false,
      layer: "l1",
      logo: "public/GET/cdn/blockchain/logos/svg/moonriver.svg",
      mainnet: null,
      metadata: {},
      name: "Moonriver",
      native_asset: "eip155:1285/slip44:1285",
      native_name: "Moonriver",
      native_token: "MOVR",
      parent: null,
      providers: {
        coingecko_native: "moonriver",
        defillama: "moonriver",
        dexscreener: "moonriver",
        moralis: "moonriver"
      },
      reorg_depth: 12,
      rpc_urls: [],
      short_name: "mriver",
      slip44: 1285,
      slug: "moonriver",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: 2,
      brand_color: "#8247E5",
      caip2: "eip155:80001",
      chain_id_hex: "0x13881",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      info_url: "https://polygon.technology",
      is_testnet: true,
      layer: "l2",
      logo: "public/GET/cdn/blockchain/logos/svg/mumbai.svg",
      mainnet: "eip155:137",
      metadata: {},
      name: "Polygon Mumbai",
      native_asset: "eip155:80001/slip44:966",
      native_name: "MATIC",
      native_token: "MATIC",
      parent: "eip155:5",
      providers: {},
      reorg_depth: 12,
      rpc_urls: [],
      short_name: "maticmum",
      slip44: 966,
      slug: "mumbai",
      status: "deprecated",
      wrapped_native_address: null
    },
    {
      aliases: [
        "near-mainnet"
      ],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "near:mainnet",
      chain_id_hex: null,
      decimals: 24,
      explorer_paths: {
        address: "address",
        block: "blocks",
        token: null,
        tx: "txns"
      },
      explorer_url: "https://nearblocks.io",
      family: "near",
      finality_blocks: null,
      info_url: "https://near.org",
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {},
      name: "NEAR",
      native_asset: "near:mainnet/slip44:397",
      native_name: "NEAR",
      native_token: "NEAR",
      parent: null,
      providers: {
        coingecko: "near-protocol",
        coingecko_native: "near",
        near_rpc: "https://archival-rpc.mainnet.near.org"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc.mainnet.fastnear.com",
        "https://archival-rpc.mainnet.near.org"
      ],
      short_name: null,
      slip44: 397,
      slug: "near",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "near-testnet"
      ],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "near:testnet",
      chain_id_hex: null,
      decimals: 24,
      explorer_paths: {
        address: "address",
        block: "blocks",
        token: null,
        tx: "txns"
      },
      explorer_url: "https://testnet.nearblocks.io",
      family: "near",
      finality_blocks: null,
      info_url: null,
      is_testnet: true,
      layer: null,
      logo: null,
      mainnet: "near:mainnet",
      metadata: {},
      name: "NEAR Testnet",
      native_asset: "near:testnet/slip44:397",
      native_name: "NEAR",
      native_token: "NEAR",
      parent: null,
      providers: {
        near_rpc: "https://archival-rpc.testnet.near.org"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc.testnet.near.org"
      ],
      short_name: null,
      slip44: 397,
      slug: "near_testnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:41443",
      chain_id_hex: "0xa1e3",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://explorer-evm.nesa.ai",
      family: "evm",
      finality_blocks: null,
      info_url: null,
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {},
      name: "Nesa",
      native_asset: null,
      native_name: null,
      native_token: "NES",
      parent: null,
      providers: {},
      reorg_depth: 12,
      rpc_urls: [],
      short_name: null,
      slip44: null,
      slug: "nesa",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:204",
      chain_id_hex: "0xcc",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://opbnbscan.com",
      family: "evm",
      finality_blocks: null,
      info_url: "https://opbnb.bnbchain.org/en",
      is_testnet: false,
      layer: "l2",
      logo: "public/GET/cdn/blockchain/logos/svg/opbnb.svg",
      mainnet: null,
      metadata: {},
      name: "opBNB",
      native_asset: "eip155:204/slip44:714",
      native_name: "BNB Chain Native Token",
      native_token: "BNB",
      parent: "eip155:56",
      providers: {
        coingecko_native: "binancecoin",
        defillama: "op_bnb"
      },
      reorg_depth: 12,
      rpc_urls: [],
      short_name: "obnb",
      slip44: 714,
      slug: "opbnb",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "op",
        "op-mainnet",
        "optimism-mainnet"
      ],
      bech32_hrp: null,
      block_time_seconds: 2,
      brand_color: "#FF0420",
      caip2: "eip155:10",
      chain_id_hex: "0xa",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://optimistic.etherscan.io",
      family: "evm",
      finality_blocks: null,
      info_url: "https://optimism.io",
      is_testnet: false,
      layer: "l2",
      logo: "public/GET/cdn/blockchain/logos/svg/optimism.svg",
      mainnet: null,
      metadata: {},
      name: "Optimism",
      native_asset: "eip155:10/slip44:60",
      native_name: "Ether",
      native_token: "ETH",
      parent: "eip155:1",
      providers: {
        alchemy: "opt-mainnet",
        arkham: "optimism",
        blockchair: "optimism",
        blockscan: "10",
        blockscout: "https://explorer.optimism.io/api",
        blocksec: "10",
        codeslaw: "optimism",
        coingecko: "optimistic-ethereum",
        coingecko_native: "ethereum",
        defillama: "optimism",
        dexscreener: "optimism",
        etherscan_scan: "optimistic.etherscan.io",
        infura: "optimism-mainnet",
        metasleuth: "optimism",
        moralis: "optimism",
        moralis_wallet: "optimism",
        sourcify: "10"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://mainnet.optimism.io",
        "https://optimism-rpc.publicnode.com"
      ],
      short_name: "oeth",
      slip44: 60,
      slug: "optimism",
      status: "active",
      wrapped_native_address: "0x4200000000000000000000000000000000000006"
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: 2,
      brand_color: "#FF0420",
      caip2: "eip155:420",
      chain_id_hex: "0x1a4",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      info_url: "https://optimism.io",
      is_testnet: true,
      layer: "l2",
      logo: "public/GET/cdn/blockchain/logos/svg/optimism.svg",
      mainnet: "eip155:10",
      metadata: {},
      name: "Optimism Goerli",
      native_asset: "eip155:420/slip44:60",
      native_name: "Goerli Ether",
      native_token: "ETH",
      parent: "eip155:5",
      providers: {},
      reorg_depth: 12,
      rpc_urls: [],
      short_name: "ogor",
      slip44: 60,
      slug: "optimism_goerli",
      status: "deprecated",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: 2,
      brand_color: "#FF0420",
      caip2: "eip155:11155420",
      chain_id_hex: "0xaa37dc",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://sepolia-optimism.etherscan.io",
      family: "evm",
      finality_blocks: null,
      info_url: "https://optimism.io",
      is_testnet: true,
      layer: "l2",
      logo: "public/GET/cdn/blockchain/logos/svg/optimism.svg",
      mainnet: "eip155:10",
      metadata: {},
      name: "Optimism Sepolia",
      native_asset: "eip155:11155420/slip44:60",
      native_name: "Sepolia Ether",
      native_token: "ETH",
      parent: "eip155:11155111",
      providers: {
        alchemy: "opt-sepolia",
        etherscan_v2: "11155420",
        infura: "optimism-sepolia",
        sourcify: "11155420"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://sepolia.optimism.io",
        "https://optimism-sepolia-rpc.publicnode.com"
      ],
      short_name: "opsep",
      slip44: 60,
      slug: "optimism_sepolia",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "osmosis-mainnet",
        "osmosis-1"
      ],
      bech32_hrp: "osmo",
      block_time_seconds: 1.6,
      brand_color: "#750BBB",
      caip2: "cosmos:osmosis-1",
      chain_id_hex: null,
      decimals: 6,
      explorer_paths: {
        token: null
      },
      explorer_url: "https://www.mintscan.io/osmosis",
      family: "cosmos",
      finality_blocks: null,
      info_url: "https://osmosis.zone",
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {},
      name: "Osmosis",
      native_asset: "cosmos:osmosis-1/slip44:118",
      native_name: "Osmosis",
      native_token: "OSMO",
      parent: null,
      providers: {
        coingecko: "osmosis",
        coingecko_native: "osmosis",
        cosmos_directory: "https://rest.cosmos.directory/osmosis",
        cosmos_lcd: "https://osmosis-rest.publicnode.com"
      },
      reorg_depth: 1,
      rpc_urls: [],
      short_name: null,
      slip44: 118,
      slug: "osmosis",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:11297108109",
      chain_id_hex: "0x2a15c308d",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      info_url: "https://palm.network",
      is_testnet: false,
      layer: "sidechain",
      logo: null,
      mainnet: null,
      metadata: {},
      name: "Palm",
      native_asset: null,
      native_name: "PALM",
      native_token: "PALM",
      parent: null,
      providers: {},
      reorg_depth: 12,
      rpc_urls: [],
      short_name: "palm",
      slip44: null,
      slug: "palm",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:3338",
      chain_id_hex: "0xd0a",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://peaq.subscan.io",
      family: "evm",
      finality_blocks: null,
      info_url: "https://www.peaq.xyz",
      is_testnet: false,
      layer: "l1",
      logo: null,
      mainnet: null,
      metadata: {},
      name: "peaq",
      native_asset: "eip155:3338/slip44:3338",
      native_name: "peaq",
      native_token: "PEAQ",
      parent: null,
      providers: {
        coingecko_native: "peaq-2"
      },
      reorg_depth: 12,
      rpc_urls: [],
      short_name: "PEAQ",
      slip44: 3338,
      slug: "peaq",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: 1,
      brand_color: null,
      caip2: "eip155:9745",
      chain_id_hex: "0x2611",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://plasmascan.to",
      family: "evm",
      finality_blocks: null,
      info_url: "https://plasma.to",
      is_testnet: false,
      layer: null,
      logo: "public/GET/cdn/blockchain/logos/svg/plasma.svg",
      mainnet: null,
      metadata: {},
      name: "Plasma",
      native_asset: null,
      native_name: "Plasma",
      native_token: "XPL",
      parent: null,
      providers: {
        coingecko_native: "plasma",
        evm_rpc: "ingest"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc.plasma.to"
      ],
      short_name: "plasma",
      slip44: null,
      slug: "plasma",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "polkadot-mainnet",
        "0x162",
        "dot"
      ],
      bech32_hrp: null,
      block_time_seconds: 6,
      brand_color: "#E6007A",
      caip2: "polkadot:91b171bb158e2d3848fa23a9f1c25182",
      chain_id_hex: null,
      decimals: 10,
      explorer_paths: {},
      explorer_url: "https://polkadot.subscan.io",
      family: "other",
      finality_blocks: null,
      info_url: "https://polkadot.com",
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {},
      name: "Polkadot",
      native_asset: "polkadot:91b171bb158e2d3848fa23a9f1c25182/slip44:354",
      native_name: "Polkadot",
      native_token: "DOT",
      parent: null,
      providers: {
        coingecko: "polkadot",
        coingecko_native: "polkadot"
      },
      reorg_depth: 12,
      rpc_urls: [],
      short_name: null,
      slip44: 354,
      slug: "polkadot",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "matic",
        "matic-network",
        "polygon-mainnet",
        "polygon-pos"
      ],
      bech32_hrp: null,
      block_time_seconds: 2,
      brand_color: "#8247E5",
      caip2: "eip155:137",
      chain_id_hex: "0x89",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://polygonscan.com",
      family: "evm",
      finality_blocks: 512,
      info_url: "https://polygon.technology",
      is_testnet: false,
      layer: "l2",
      logo: "public/GET/cdn/blockchain/logos/svg/polygon.svg",
      mainnet: null,
      metadata: {},
      name: "Polygon",
      native_asset: "eip155:137/slip44:966",
      native_name: "POL",
      native_token: "POL",
      parent: "eip155:1",
      providers: {
        alchemy: "polygon-mainnet",
        arkham: "polygon",
        blockchair: "polygon",
        blockscan: "137",
        blockscout: "https://polygon.blockscout.com/api",
        blocksec: "137",
        codeslaw: "polygon",
        coingecko: "polygon-pos",
        coingecko_native: "polygon-ecosystem-token",
        defillama: "polygon",
        dexscreener: "polygon",
        etherscan_scan: "polygonscan.com",
        infura: "polygon-mainnet",
        metasleuth: "polygon",
        moralis: "polygon",
        moralis_wallet: "polygon",
        sourcify: "137"
      },
      reorg_depth: 256,
      rpc_urls: [
        "https://polygon-bor-rpc.publicnode.com",
        "https://polygon.drpc.org",
        "https://polygon.gateway.tenderly.co"
      ],
      short_name: "pol",
      slip44: 966,
      slug: "polygon",
      status: "active",
      wrapped_native_address: "0x0d500b1d8e8ef31e21c99d1db9a6444d3adf1270"
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: 2,
      brand_color: "#8247E5",
      caip2: "eip155:80002",
      chain_id_hex: "0x13882",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://amoy.polygonscan.com",
      family: "evm",
      finality_blocks: null,
      info_url: "https://polygon.technology",
      is_testnet: true,
      layer: "l2",
      logo: "public/GET/cdn/blockchain/logos/svg/polygon-amoy.svg",
      mainnet: "eip155:137",
      metadata: {},
      name: "Polygon Amoy",
      native_asset: "eip155:80002/slip44:966",
      native_name: "POL",
      native_token: "POL",
      parent: "eip155:11155111",
      providers: {
        alchemy: "polygon-amoy",
        infura: "polygon-amoy",
        moralis: "polygon_amoy",
        sourcify: "80002"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://polygon-amoy.drpc.org",
        "https://polygon-amoy-bor-rpc.publicnode.com"
      ],
      short_name: "polygonamoy",
      slip44: 966,
      slug: "polygon_amoy",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "polygon-zkevm",
        "zkevm"
      ],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:1101",
      chain_id_hex: "0x44d",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://zkevm.polygonscan.com",
      family: "evm",
      finality_blocks: null,
      info_url: "https://polygon.technology/polygon-zkevm",
      is_testnet: false,
      layer: "l2",
      logo: null,
      mainnet: null,
      metadata: {},
      name: "Polygon zkEVM",
      native_asset: "eip155:1101/slip44:60",
      native_name: "Ether",
      native_token: "ETH",
      parent: "eip155:1",
      providers: {
        arkham: "polygon_zkevm",
        coingecko_native: "ethereum"
      },
      reorg_depth: 12,
      rpc_urls: [],
      short_name: "zkevm",
      slip44: 60,
      slug: "polygon_zkevm",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:2442",
      chain_id_hex: "0x98a",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      info_url: "https://polygon.technology/polygon-zkevm",
      is_testnet: true,
      layer: "l2",
      logo: null,
      mainnet: "eip155:1101",
      metadata: {},
      name: "Polygon zkEVM Cardona",
      native_asset: "eip155:2442/slip44:60",
      native_name: "Ether",
      native_token: "ETH",
      parent: "eip155:11155111",
      providers: {},
      reorg_depth: 12,
      rpc_urls: [],
      short_name: "zkevm-testnet-cardona",
      slip44: 60,
      slug: "polygon_zkevm_cardona",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "pls"
      ],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:369",
      chain_id_hex: "0x171",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://scan.pulsechain.com",
      family: "evm",
      finality_blocks: null,
      info_url: "https://pulsechain.com",
      is_testnet: false,
      layer: "l1",
      logo: "public/GET/cdn/blockchain/logos/svg/pulsechain.svg",
      mainnet: null,
      metadata: {},
      name: "PulseChain",
      native_asset: "eip155:369/slip44:1028",
      native_name: "Pulse",
      native_token: "PLS",
      parent: null,
      providers: {
        arkham: "pulsechain",
        coingecko_native: "pulsechain",
        moralis: "pulsechain",
        moralis_wallet: "pulsechain"
      },
      reorg_depth: 12,
      rpc_urls: [],
      short_name: "pls",
      slip44: 1028,
      slug: "pulsechain",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:4663",
      chain_id_hex: "0x1237",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://robinhoodchain.blockscout.com",
      family: "evm",
      finality_blocks: null,
      info_url: "https://docs.robinhood.com/chain",
      is_testnet: false,
      layer: "l2",
      logo: null,
      mainnet: null,
      metadata: {},
      name: "Robinhood Chain",
      native_asset: "eip155:4663/slip44:60",
      native_name: "Ether",
      native_token: "ETH",
      parent: "eip155:1",
      providers: {
        blockscout_v2: "https://robinhoodchain.blockscout.com/api/v2",
        coingecko: "robinhood",
        coingecko_native: "ethereum",
        defillama: "robinhood",
        evm_rpc: "discovery",
        moralis: "robinhood",
        moralis_wallet: "robinhood"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc.mainnet.chain.robinhood.com"
      ],
      short_name: "robinhoodchain",
      slip44: 60,
      slug: "robinhood",
      status: "active",
      wrapped_native_address: "0x0bd7d308f8e1639fab988df18a8011f41eacad73"
    },
    {
      aliases: [
        "ron"
      ],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:2020",
      chain_id_hex: "0x7e4",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://app.roninchain.com",
      family: "evm",
      finality_blocks: null,
      info_url: "https://roninchain.com",
      is_testnet: false,
      layer: "l1",
      logo: "public/GET/cdn/blockchain/logos/svg/ronin.svg",
      mainnet: null,
      metadata: {},
      name: "Ronin",
      native_asset: null,
      native_name: "RON",
      native_token: "RON",
      parent: null,
      providers: {
        arkham: "ronin",
        coingecko_native: "ronin",
        defillama: "ronin",
        moralis: "ronin",
        moralis_wallet: "ronin"
      },
      reorg_depth: 12,
      rpc_urls: [],
      short_name: "ron",
      slip44: null,
      slug: "ronin",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:2021",
      chain_id_hex: "0x7e5",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://saigon-app.roninchain.com",
      family: "evm",
      finality_blocks: null,
      info_url: "https://roninchain.com",
      is_testnet: true,
      layer: "l1",
      logo: "public/GET/cdn/blockchain/logos/svg/ronin-saigon.svg",
      mainnet: "eip155:2020",
      metadata: {},
      name: "Ronin Saigon Testnet",
      native_asset: null,
      native_name: "RON",
      native_token: "RON",
      parent: null,
      providers: {
        moralis: "ronin_saigon"
      },
      reorg_depth: 12,
      rpc_urls: [],
      short_name: null,
      slip44: null,
      slug: "ronin_saigon",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "scroll-mainnet"
      ],
      bech32_hrp: null,
      block_time_seconds: 3,
      brand_color: "#EBC28E",
      caip2: "eip155:534352",
      chain_id_hex: "0x82750",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://scrollscan.com",
      family: "evm",
      finality_blocks: null,
      info_url: "https://scroll.io",
      is_testnet: false,
      layer: "l2",
      logo: "public/GET/cdn/blockchain/logos/svg/scroll.svg",
      mainnet: null,
      metadata: {},
      name: "Scroll",
      native_asset: "eip155:534352/slip44:60",
      native_name: "Ether",
      native_token: "ETH",
      parent: "eip155:1",
      providers: {
        codeslaw: "scroll",
        coingecko_native: "ethereum",
        defillama: "scroll",
        dexscreener: "scroll",
        etherscan_scan: "scrollscan.com",
        evm_rpc: "ingest"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc.scroll.io"
      ],
      short_name: "scr",
      slip44: 60,
      slug: "scroll",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: 3,
      brand_color: "#EBC28E",
      caip2: "eip155:534351",
      chain_id_hex: "0x8274f",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      info_url: "https://scroll.io",
      is_testnet: true,
      layer: "l2",
      logo: "public/GET/cdn/blockchain/logos/svg/scroll-testnet.svg",
      mainnet: "eip155:534352",
      metadata: {},
      name: "Scroll Sepolia",
      native_asset: "eip155:534351/slip44:60",
      native_name: "Ether",
      native_token: "ETH",
      parent: "eip155:11155111",
      providers: {},
      reorg_depth: 12,
      rpc_urls: [],
      short_name: "scr-sepolia",
      slip44: 60,
      slug: "scroll_testnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "sei-mainnet"
      ],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:1329",
      chain_id_hex: "0x531",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://seitrace.com",
      family: "evm",
      finality_blocks: null,
      info_url: "https://www.sei.io",
      is_testnet: false,
      layer: "l1",
      logo: "public/GET/cdn/blockchain/logos/svg/sei.svg",
      mainnet: null,
      metadata: {},
      name: "Sei",
      native_asset: "eip155:1329/slip44:19000118",
      native_name: "Sei",
      native_token: "SEI",
      parent: null,
      providers: {
        coingecko_native: "sei-network",
        moralis: "sei",
        moralis_wallet: "sei"
      },
      reorg_depth: 12,
      rpc_urls: [],
      short_name: "sei",
      slip44: 19000118,
      slug: "sei",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:1328",
      chain_id_hex: "0x530",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      info_url: "https://www.sei.io",
      is_testnet: true,
      layer: "l1",
      logo: "public/GET/cdn/blockchain/logos/svg/sei-testnet.svg",
      mainnet: "eip155:1329",
      metadata: {},
      name: "Sei Testnet",
      native_asset: "eip155:1328/slip44:19000118",
      native_name: "Sei",
      native_token: "SEI",
      parent: null,
      providers: {
        moralis: "sei_testnet"
      },
      reorg_depth: 12,
      rpc_urls: [],
      short_name: "sei-testnet",
      slip44: 19000118,
      slug: "sei_testnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: 12,
      brand_color: "#627EEA",
      caip2: "eip155:11155111",
      chain_id_hex: "0xaa36a7",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://sepolia.etherscan.io",
      family: "evm",
      finality_blocks: null,
      info_url: "https://sepolia.otterscan.io",
      is_testnet: true,
      layer: "l1",
      logo: "public/GET/cdn/blockchain/logos/svg/sepolia.svg",
      mainnet: "eip155:1",
      metadata: {},
      name: "Sepolia",
      native_asset: "eip155:11155111/slip44:60",
      native_name: "Sepolia Ether",
      native_token: "ETH",
      parent: null,
      providers: {
        alchemy: "eth-sepolia",
        ankr: "eth_sepolia",
        etherscan_v2: "11155111",
        infura: "sepolia",
        moralis: "sepolia",
        sourcify: "11155111"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://ethereum-sepolia-rpc.publicnode.com"
      ],
      short_name: "sep",
      slip44: 60,
      slug: "sepolia",
      status: "active",
      wrapped_native_address: "0xfff9976782d46cc05630d1f6ebab18b2324d6b14"
    },
    {
      aliases: [
        "solana-mainnet",
        "0x1399c",
        "sol"
      ],
      bech32_hrp: null,
      block_time_seconds: 0.4,
      brand_color: "#14F195",
      caip2: "solana:5eykt4UsFv8P8NJdTREpY1vzqKqZKvdp",
      chain_id_hex: null,
      decimals: 9,
      explorer_paths: {
        address: "account"
      },
      explorer_url: "https://solscan.io",
      family: "solana",
      finality_blocks: null,
      info_url: "https://solana.com",
      is_testnet: false,
      layer: null,
      logo: "public/GET/cdn/blockchain/logos/svg/solana.svg",
      mainnet: null,
      metadata: {},
      name: "Solana",
      native_asset: "solana:5eykt4UsFv8P8NJdTREpY1vzqKqZKvdp/slip44:501",
      native_name: "Solana",
      native_token: "SOL",
      parent: null,
      providers: {
        arkham: "solana",
        blockchair: "solana",
        coingecko: "solana",
        coingecko_native: "solana",
        defillama: "solana",
        dexscreener: "solana",
        helius: "mainnet"
      },
      reorg_depth: 32,
      rpc_urls: [
        "https://solana-rpc.publicnode.com",
        "https://api.mainnet-beta.solana.com"
      ],
      short_name: null,
      slip44: 501,
      slug: "solana",
      status: "active",
      wrapped_native_address: "So11111111111111111111111111111111111111112"
    },
    {
      aliases: [
        "solana-devnet"
      ],
      bech32_hrp: null,
      block_time_seconds: 0.4,
      brand_color: "#14F195",
      caip2: "solana:EtWTRABZaYq6iMfeYKouRu166VU2xqa1",
      chain_id_hex: null,
      decimals: 9,
      explorer_paths: {},
      explorer_url: null,
      family: "solana",
      finality_blocks: null,
      info_url: null,
      is_testnet: true,
      layer: null,
      logo: "public/GET/cdn/blockchain/logos/svg/solana-devnet.svg",
      mainnet: "solana:5eykt4UsFv8P8NJdTREpY1vzqKqZKvdp",
      metadata: {},
      name: "Solana Devnet",
      native_asset: "solana:EtWTRABZaYq6iMfeYKouRu166VU2xqa1/slip44:501",
      native_name: "Solana",
      native_token: "SOL",
      parent: null,
      providers: {
        helius: "devnet"
      },
      reorg_depth: 32,
      rpc_urls: [
        "https://api.devnet.solana.com"
      ],
      short_name: null,
      slip44: 501,
      slug: "solana_devnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "solana-testnet"
      ],
      bech32_hrp: null,
      block_time_seconds: 0.4,
      brand_color: "#14F195",
      caip2: "solana:4uhcVJyU9pJkvQyS88uRDiswHXSCkY3z",
      chain_id_hex: null,
      decimals: 9,
      explorer_paths: {},
      explorer_url: null,
      family: "solana",
      finality_blocks: null,
      info_url: null,
      is_testnet: true,
      layer: null,
      logo: "public/GET/cdn/blockchain/logos/svg/solana.svg",
      mainnet: "solana:5eykt4UsFv8P8NJdTREpY1vzqKqZKvdp",
      metadata: {},
      name: "Solana Testnet",
      native_asset: "solana:4uhcVJyU9pJkvQyS88uRDiswHXSCkY3z/slip44:501",
      native_name: "Solana",
      native_token: "SOL",
      parent: null,
      providers: {
        helius: "testnet"
      },
      reorg_depth: 32,
      rpc_urls: [
        "https://api.testnet.solana.com"
      ],
      short_name: null,
      slip44: 501,
      slug: "solana_testnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: 2,
      brand_color: null,
      caip2: "eip155:146",
      chain_id_hex: "0x92",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://sonicscan.org",
      family: "evm",
      finality_blocks: null,
      info_url: "https://soniclabs.com",
      is_testnet: false,
      layer: "l1",
      logo: null,
      mainnet: null,
      metadata: {},
      name: "Sonic",
      native_asset: null,
      native_name: "Sonic",
      native_token: "S",
      parent: null,
      providers: {
        evm_rpc: "ingest"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc.soniclabs.com"
      ],
      short_name: null,
      slip44: null,
      slug: "sonic",
      status: "active",
      wrapped_native_address: "0x039e2fb66102314ce7b64ce5ce3e5183bc94ad38"
    },
    {
      aliases: [
        "thorchain-mainnet"
      ],
      bech32_hrp: "thor",
      block_time_seconds: null,
      brand_color: null,
      caip2: "cosmos:thorchain-1",
      chain_id_hex: null,
      decimals: 8,
      explorer_paths: {
        address: "address",
        block: "block",
        token: null,
        tx: "tx"
      },
      explorer_url: "https://thorchain.net",
      family: "cosmos",
      finality_blocks: null,
      info_url: "https://thorchain.org",
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {},
      name: "THORChain",
      native_asset: "cosmos:thorchain-1/slip44:931",
      native_name: "Rune",
      native_token: "RUNE",
      parent: null,
      providers: {
        coingecko: "thorchain",
        coingecko_native: "thorchain",
        cosmos_directory: "https://rest.cosmos.directory/thorchain",
        cosmos_lcd: "https://gateway.liquify.com/chain/thorchain_api"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://gateway.liquify.com/chain/thorchain_api"
      ],
      short_name: null,
      slip44: 931,
      slug: "thorchain",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "trx",
        "tron-mainnet",
        "tron:0x2b6653dc"
      ],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "tron:728126428",
      chain_id_hex: null,
      decimals: 6,
      explorer_paths: {
        token: "token20",
        tx: "transaction"
      },
      explorer_url: "https://tronscan.org/#",
      family: "tron",
      finality_blocks: null,
      info_url: "https://tron.network",
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {},
      name: "TRON",
      native_asset: "tron:728126428/slip44:195",
      native_name: "Tronix",
      native_token: "TRX",
      parent: null,
      providers: {
        blockchair: "tron",
        coingecko: "tron",
        coingecko_native: "tron",
        tronscan: "mainnet"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://api.trongrid.io"
      ],
      short_name: null,
      slip44: 195,
      slug: "tron",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "tron-nile",
        "tron-testnet-nile",
        "tron:0xcd8690dc"
      ],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "tron:3448148188",
      chain_id_hex: null,
      decimals: 6,
      explorer_paths: {
        token: "token20",
        tx: "transaction"
      },
      explorer_url: "https://nile.tronscan.org/#",
      family: "tron",
      finality_blocks: null,
      info_url: null,
      is_testnet: true,
      layer: null,
      logo: null,
      mainnet: "tron:728126428",
      metadata: {},
      name: "TRON Nile",
      native_asset: "tron:3448148188/slip44:195",
      native_name: "Tronix",
      native_token: "TRX",
      parent: null,
      providers: {
        tronscan: "nile"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://nile.trongrid.io"
      ],
      short_name: null,
      slip44: 195,
      slug: "tron_nile",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "tron-shasta",
        "tron-testnet-shasta",
        "tron:0x94a9059e"
      ],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "tron:2494104990",
      chain_id_hex: null,
      decimals: 6,
      explorer_paths: {
        token: "token20",
        tx: "transaction"
      },
      explorer_url: "https://shasta.tronscan.org/#",
      family: "tron",
      finality_blocks: null,
      info_url: null,
      is_testnet: true,
      layer: null,
      logo: null,
      mainnet: "tron:728126428",
      metadata: {},
      name: "TRON Shasta",
      native_asset: "tron:2494104990/slip44:195",
      native_name: "Tronix",
      native_token: "TRX",
      parent: null,
      providers: {
        tronscan: "shasta"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://api.shasta.trongrid.io"
      ],
      short_name: null,
      slip44: 195,
      slug: "tron_shasta",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: 1,
      brand_color: null,
      caip2: "eip155:130",
      chain_id_hex: "0x82",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://unichain.blockscout.com",
      family: "evm",
      finality_blocks: null,
      info_url: "https://unichain.org",
      is_testnet: false,
      layer: "l2",
      logo: null,
      mainnet: null,
      metadata: {},
      name: "Unichain",
      native_asset: "eip155:130/slip44:60",
      native_name: "Ether",
      native_token: "ETH",
      parent: "eip155:1",
      providers: {
        blockscout_v2: "https://unichain.blockscout.com/api/v2",
        coingecko_native: "ethereum",
        evm_rpc: "discovery"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://mainnet.unichain.org"
      ],
      short_name: null,
      slip44: 60,
      slug: "unichain",
      status: "active",
      wrapped_native_address: "0x4200000000000000000000000000000000000006"
    },
    {
      aliases: [
        "xrpl",
        "ripple",
        "xrp-mainnet",
        "xrpl-mainnet"
      ],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "xrpl:0",
      chain_id_hex: null,
      decimals: 6,
      explorer_paths: {
        address: "account",
        block: "ledger",
        token: null
      },
      explorer_url: "https://xrpscan.com",
      family: "xrpl",
      finality_blocks: null,
      info_url: "https://xrpl.org",
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {},
      name: "XRP Ledger",
      native_asset: "xrpl:0/slip44:144",
      native_name: "XRP",
      native_token: "XRP",
      parent: null,
      providers: {
        blockchair: "ripple",
        coingecko: "xrp",
        coingecko_native: "ripple",
        xrpscan: "mainnet"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://s1.ripple.com:51234",
        "https://s2.ripple.com:51234"
      ],
      short_name: null,
      slip44: 144,
      slug: "xrp",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "xrp-testnet",
        "xrpl-testnet"
      ],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "xrpl:1",
      chain_id_hex: null,
      decimals: 6,
      explorer_paths: {
        address: "account",
        block: "ledger",
        token: null
      },
      explorer_url: "https://testnet.xrpscan.com",
      family: "xrpl",
      finality_blocks: null,
      info_url: null,
      is_testnet: true,
      layer: null,
      logo: null,
      mainnet: "xrpl:0",
      metadata: {},
      name: "XRP Ledger Testnet",
      native_asset: "xrpl:1/slip44:144",
      native_name: "XRP",
      native_token: "XRP",
      parent: null,
      providers: {
        xrpscan: "testnet"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://s.altnet.rippletest.net:51234"
      ],
      short_name: null,
      slip44: 144,
      slug: "xrp_testnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "zcash-mainnet",
        "zec"
      ],
      bech32_hrp: null,
      block_time_seconds: 75,
      brand_color: "#F4B728",
      caip2: "bip122:00040fe8ec8471911baa1db1266ea15d",
      chain_id_hex: null,
      decimals: 8,
      explorer_paths: {
        token: null,
        tx: "transaction"
      },
      explorer_url: "https://blockchair.com/zcash",
      family: "utxo",
      finality_blocks: null,
      info_url: "https://z.cash",
      is_testnet: false,
      layer: null,
      logo: null,
      mainnet: null,
      metadata: {},
      name: "Zcash",
      native_asset: "bip122:00040fe8ec8471911baa1db1266ea15d/slip44:133",
      native_name: "Zcash",
      native_token: "ZEC",
      parent: null,
      providers: {
        blockchair: "zcash",
        blockchair_api: "zcash",
        coingecko_native: "zcash"
      },
      reorg_depth: 24,
      rpc_urls: [],
      short_name: null,
      slip44: 133,
      slug: "zcash",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:7000",
      chain_id_hex: "0x1b58",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://explorer.zetachain.com",
      family: "evm",
      finality_blocks: null,
      info_url: "https://zetachain.com/docs",
      is_testnet: false,
      layer: "l1",
      logo: "public/GET/cdn/blockchain/logos/svg/zetachain.svg",
      mainnet: null,
      metadata: {},
      name: "ZetaChain",
      native_asset: "eip155:7000/slip44:7000",
      native_name: "Zeta",
      native_token: "ZETA",
      parent: null,
      providers: {
        coingecko_native: "zetachain"
      },
      reorg_depth: 12,
      rpc_urls: [],
      short_name: "zetachain-mainnet",
      slip44: 7e3,
      slug: "zetachain",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:7001",
      chain_id_hex: "0x1b59",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://testnet.zetascan.com",
      family: "evm",
      finality_blocks: null,
      info_url: "https://zetachain.com/docs",
      is_testnet: true,
      layer: "l1",
      logo: "public/GET/cdn/blockchain/logos/svg/zetachain.svg",
      mainnet: "eip155:7000",
      metadata: {},
      name: "Zetachain Testnet",
      native_asset: "eip155:7001/slip44:7000",
      native_name: "Zeta",
      native_token: "ZETA",
      parent: null,
      providers: {},
      reorg_depth: 12,
      rpc_urls: [],
      short_name: "zetachain-testnet",
      slip44: 7e3,
      slug: "zetachain_testnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "zksync",
        "zksync-era",
        "era"
      ],
      bech32_hrp: null,
      block_time_seconds: 1,
      brand_color: "#8C8DFC",
      caip2: "eip155:324",
      chain_id_hex: "0x144",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://explorer.zksync.io",
      family: "evm",
      finality_blocks: null,
      info_url: "https://zksync.io",
      is_testnet: false,
      layer: "l2",
      logo: "public/GET/cdn/blockchain/logos/svg/zksync-era.svg",
      mainnet: null,
      metadata: {},
      name: "zkSync Era",
      native_asset: "eip155:324/slip44:60",
      native_name: "Ether",
      native_token: "ETH",
      parent: "eip155:1",
      providers: {
        alchemy: "zksync-mainnet",
        arkham: "zksync_era",
        coingecko: "zksync",
        coingecko_native: "ethereum",
        defillama: "era",
        dexscreener: "zksync",
        infura: "zksync-mainnet",
        sourcify: "324"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://mainnet.era.zksync.io"
      ],
      short_name: "zksync",
      slip44: 60,
      slug: "zksync_era",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: 1,
      brand_color: "#8C8DFC",
      caip2: "eip155:300",
      chain_id_hex: "0x12c",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://sepolia.explorer.zksync.io",
      family: "evm",
      finality_blocks: null,
      info_url: "https://zksync.io",
      is_testnet: true,
      layer: "l2",
      logo: "public/GET/cdn/blockchain/logos/svg/zksync-sepolia.svg",
      mainnet: "eip155:324",
      metadata: {},
      name: "zkSync Sepolia",
      native_asset: "eip155:300/slip44:60",
      native_name: "Ether",
      native_token: "ETH",
      parent: "eip155:11155111",
      providers: {},
      reorg_depth: 12,
      rpc_urls: [],
      short_name: "zksync-sepolia",
      slip44: 60,
      slug: "zksync_sepolia",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: 1,
      brand_color: "#8C8DFC",
      caip2: "eip155:280",
      chain_id_hex: "0x118",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      info_url: "https://zksync.io",
      is_testnet: true,
      layer: "l2",
      logo: "public/GET/cdn/blockchain/logos/svg/zksync-era.svg",
      mainnet: "eip155:324",
      metadata: {},
      name: "zkSync Goerli",
      native_asset: "eip155:280/slip44:60",
      native_name: "Ether",
      native_token: "ETH",
      parent: "eip155:1",
      providers: {},
      reorg_depth: 12,
      rpc_urls: [],
      short_name: "zksync-goerli",
      slip44: 60,
      slug: "zksync_testnet",
      status: "deprecated",
      wrapped_native_address: null
    }
  ],
  provider_keys: {
    alchemy: "Alchemy network subdomain: https://{id}.g.alchemy.com/v2/<key>",
    ankr: "Ankr RPC path: https://rpc.ankr.com/{id}/<key>",
    arkham: "Arkham chain name",
    blockchair: "Blockchair chain name",
    blockchair_api: "Blockchair API chain (UTXO ingest alongside xmrchain)",
    blockscan: "Blockscan chain id",
    blockscout: "Blockscout Etherscan-compatible API base (ABIs, source)",
    blockscout_v2: "Blockscout v2 API base that ingests the chain's transactions",
    blocksec: "BlockSec (MetaSleuth API) chain id",
    codeslaw: "Codeslaw platform name",
    coingecko: "CoinGecko asset platform id",
    coingecko_native: "CoinGecko coin id of the chain's native (gas) coin \u2014 what prices its native transfers. Measured per chain, not derived from the platform: CoinGecko's own platform \u2192 native-coin map names AURORA for Aurora and wrapped SEI for Sei, whose gas is ETH and SEI",
    cosmos_directory: "cosmos.directory LCD proxy base URL, the Cosmos fallback",
    cosmos_lcd: "Cosmos SDK LCD (REST) base URL, first choice (Cosmos ingest)",
    defillama: "DefiLlama chain name",
    dexscreener: "DexScreener chain id",
    esplora: "Esplora network (UTXO ingest)",
    etherscan_scan: "Etherscan-family explorer host scraped for labels",
    etherscan_v2: "Etherscan API V2 chainid (decimal) the chain's transactions are read through by hash, on the platform's Etherscan key. The Etherscan route ingests a chain carrying it that no Moralis / Blockscout / JSON-RPC route claims first",
    evm_rpc: "'ingest' (transactions read over the chain's JSON-RPC) or 'discovery' (block windows found over it, transactions read through another route)",
    helius: "Helius cluster (Solana ingest)",
    hyperliquid: "Hyperliquid network (HyperCore ingest)",
    infura: "Infura network subdomain: https://{id}.infura.io/v3/<key>",
    metasleuth: "MetaSleuth chain name",
    moralis: "Moralis chain name; the Moralis ingest route reads every chain carrying it",
    moralis_wallet: "Moralis chain name, where Moralis's mainnet-only WALLET endpoints (wallet chains / history / net worth) accept the chain \u2014 measured per chain, because Moralis's idea of mainnet is not the registry's (it refuses Moonriver)",
    near_rpc: "NEAR archival RPC URL (NEAR ingest)",
    scanscope: "ScanScope chain name",
    sourcify: "Sourcify chain id",
    tronscan: "TronScan network (TRON ingest)",
    xmrchain: "xmrchain onion-explorer network (Monero ingest)",
    xrpscan: "XRPScan network (XRP Ledger ingest)"
  }
};

// src/chains/catalogue.ts
var CatalogueSchema = z.object({
  chains: z.array(ChainWireSchema).default([]),
  provider_keys: z.record(z.string(), z.string()).default({})
});
var SNAPSHOT = CatalogueSchema.parse(catalogue_generated_default);
var BUNDLED_SLUG_BY_CAIP2 = new Map(
  SNAPSHOT.chains.map((row) => [row.caip2, row.slug])
);
var BUNDLED_CHAINS = Object.freeze(
  SNAPSHOT.chains.map((row) => chainFromWire(row, (caip2) => BUNDLED_SLUG_BY_CAIP2.get(caip2)))
);
var CHAIN_PROVIDER_KEYS = Object.freeze({
  ...SNAPSHOT.provider_keys
});

// src/chains/fact-links.generated.json
var fact_links_generated_default = {};

// src/chains/fact-links.ts
var VALUE_TOKEN = "{value}";
function str(value) {
  return typeof value === "string" && value.trim() ? value : null;
}
function build(wire) {
  const links = {};
  const patterns = /* @__PURE__ */ new Map();
  const rejected = [];
  for (const [protocolId, facts] of Object.entries(wire)) {
    for (const [fact, row] of Object.entries(facts ?? {})) {
      const urlTemplate = str(row?.url_template);
      const explorer = str(row?.explorer);
      const valuePattern = str(row?.value_pattern);
      if (!urlTemplate || !explorer || !urlTemplate.startsWith("https://") || !urlTemplate.includes(VALUE_TOKEN)) {
        rejected.push(`${protocolId}.${fact}`);
        continue;
      }
      let compiled = null;
      if (valuePattern) {
        try {
          compiled = new RegExp(`^(?:${valuePattern})$`);
        } catch {
          rejected.push(`${protocolId}.${fact}`);
          continue;
        }
      }
      const link = Object.freeze({
        protocolId,
        fact,
        explorer,
        urlTemplate,
        valuePattern,
        via: str(row?.via)
      });
      if (compiled) patterns.set(link, compiled);
      (links[protocolId] ??= {})[fact] = link;
    }
  }
  for (const facts of Object.values(links)) Object.freeze(facts);
  return { links: Object.freeze(links), patterns, rejected };
}
var BUILT = build(fact_links_generated_default);
var PROTOCOL_FACT_LINKS = BUILT.links;
function rejectedFactLinks() {
  return BUILT.rejected;
}
function getFactLink(protocolId, fact) {
  if (!protocolId || !fact) return null;
  return PROTOCOL_FACT_LINKS[protocolId]?.[fact] ?? null;
}
function factLinkMatches(link, value) {
  const text = value?.trim();
  if (!text) return false;
  const pattern = BUILT.patterns.get(link);
  return pattern ? pattern.test(text) : true;
}
function factLinkHref(link, value) {
  if (!factLinkMatches(link, value)) return null;
  const encoded = value.trim().split("/").map(encodeURIComponent).join("/");
  return link.urlTemplate.replace(VALUE_TOKEN, encoded);
}
function getFactLinkUrl(protocolId, fact, value) {
  const link = getFactLink(protocolId, fact);
  return link ? factLinkHref(link, value) : null;
}

// src/logging/index.ts
var LEVEL_WEIGHT = { debug: 10, info: 20, warn: 30, error: 40 };
var DEBUG_FLAG_KEY = "authe:debug";
function storage() {
  try {
    const s = typeof localStorage !== "undefined" ? localStorage : void 0;
    return s && typeof s.getItem === "function" ? s : void 0;
  } catch {
    return void 0;
  }
}
function isDebugEnabled() {
  try {
    if (typeof location !== "undefined") {
      const param = new URLSearchParams(location.search).get("debug");
      if (param === "1") {
        enableDebug();
        return true;
      }
      if (param === "0") {
        disableDebug();
        return false;
      }
    }
  } catch {
  }
  return storage()?.getItem(DEBUG_FLAG_KEY) === "1";
}
function enableDebug() {
  storage()?.setItem(DEBUG_FLAG_KEY, "1");
}
function disableDebug() {
  storage()?.removeItem(DEBUG_FLAG_KEY);
}
var logging = globalSingleton("logging/state", () => ({
  sinks: [],
  /** Minimum level that reaches the console (debug-flag lowers it to `debug`). */
  minConsoleLevel: "warn"
}));
function createLogger(namespace) {
  function emit(level, message, context) {
    const record = { level, namespace, message, context, timestamp: Date.now() };
    for (const sink of logging.sinks) {
      try {
        sink(record);
      } catch {
      }
    }
    const threshold = isDebugEnabled() ? LEVEL_WEIGHT.debug : LEVEL_WEIGHT[logging.minConsoleLevel];
    if (LEVEL_WEIGHT[level] < threshold) return;
    const prefix = `[${namespace}]`;
    const fn = level === "debug" ? console.debug : level === "info" ? console.info : level === "warn" ? console.warn : console.error;
    if (context) fn(prefix, message, context);
    else fn(prefix, message);
  }
  return {
    debug: (m, c) => emit("debug", m, c),
    info: (m, c) => emit("info", m, c),
    warn: (m, c) => emit("warn", m, c),
    error: (m, c) => emit("error", m, c),
    child: (suffix) => createLogger(`${namespace}:${suffix}`)
  };
}

// src/chains/registry.ts
var log = createLogger("chains:registry");
function chainIsEvm(chain) {
  return chain.family === ChainFamily.EVM;
}
function hexKey(value) {
  let token = value.trim().toLowerCase();
  if (!token) return null;
  if (!token.startsWith("0x")) token = `0x${token}`;
  try {
    return `0x${BigInt(token).toString(16)}`;
  } catch {
    return null;
  }
}
var ChainRegistry = class {
  byCaip2 = /* @__PURE__ */ new Map();
  bySlug = /* @__PURE__ */ new Map();
  byHex = /* @__PURE__ */ new Map();
  // Alternate spellings; consulted by `resolve` only after the canonical
  // lookups, so an alias can never shadow a caip2 / hex / slug.
  byAlias = /* @__PURE__ */ new Map();
  listeners = /* @__PURE__ */ new Set();
  revision = 0;
  batchDepth = 0;
  pending = false;
  sorted = null;
  constructor(chains = []) {
    for (const chain of chains) this.register(chain);
    this.pending = false;
  }
  /**
   * Changes whenever the answer to any lookup may have changed — a register,
   * an unregister, a hydration. What `useSyncExternalStore` snapshots.
   */
  get version() {
    return this.revision;
  }
  /**
   * Add `chain`; `replace: true` overwrites the row with the same CAIP-2 id.
   *
   * Throws when the chain's slug or hex id already names a DIFFERENT chain —
   * two rows can never share a spelling, with or without `replace` (Python's
   * `ChainRegistry.register` refuses the same).
   */
  register(chain, { replace = false } = {}) {
    const caip2 = normalizeCaip2(chain.caip2);
    const existing = this.byCaip2.get(caip2);
    if (existing && existing !== chain && !replace) {
      throw new Error(`Chain ${caip2} is already registered; pass replace=true to overwrite.`);
    }
    const hex = chain.chainIdHex ? hexKey(chain.chainIdHex) : null;
    const slugOwner = this.bySlug.get(chain.slug);
    if (slugOwner && normalizeCaip2(slugOwner.caip2) !== caip2) {
      throw new Error(`Chain slug "${chain.slug}" of ${caip2} already names ${slugOwner.caip2}.`);
    }
    const hexOwner = hex ? this.byHex.get(hex) : void 0;
    if (hexOwner && normalizeCaip2(hexOwner.caip2) !== caip2) {
      throw new Error(`Chain hex ${hex} of ${caip2} already names ${hexOwner.caip2}.`);
    }
    if (existing) this.drop(existing);
    this.byCaip2.set(caip2, chain);
    this.bySlug.set(chain.slug, chain);
    if (hex) this.byHex.set(hex, chain);
    for (const alias of foldAliases(chain.aliases)) this.byAlias.set(alias, chain);
    this.touch();
  }
  /** Remove and return the chain `value` resolves to (any spelling). */
  unregister(value) {
    const chain = this.resolve(value);
    if (!chain) return null;
    this.drop(chain);
    this.touch();
    return chain;
  }
  drop(chain) {
    this.byCaip2.delete(normalizeCaip2(chain.caip2));
    if (this.bySlug.get(chain.slug) === chain) this.bySlug.delete(chain.slug);
    const hex = chain.chainIdHex ? hexKey(chain.chainIdHex) : null;
    if (hex && this.byHex.get(hex) === chain) this.byHex.delete(hex);
    for (const alias of foldAliases(chain.aliases)) {
      if (this.byAlias.get(alias) === chain) this.byAlias.delete(alias);
    }
  }
  /** Drop every registered chain (test-only). */
  clear() {
    this.byCaip2.clear();
    this.bySlug.clear();
    this.byHex.clear();
    this.byAlias.clear();
    this.touch();
  }
  /**
   * Run `fn` and notify subscribers ONCE at the end, however many rows it
   * changed — a hydration of a hundred rows is one re-render, not a hundred.
   */
  batch(fn) {
    this.batchDepth += 1;
    try {
      return fn();
    } finally {
      this.batchDepth -= 1;
      if (this.batchDepth === 0 && this.pending) {
        this.pending = false;
        this.emit();
      }
    }
  }
  /** Call `listener` after every change; returns the unsubscribe. */
  subscribe(listener) {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }
  touch() {
    this.revision += 1;
    this.sorted = null;
    if (this.batchDepth > 0) this.pending = true;
    else this.emit();
  }
  emit() {
    for (const listener of [...this.listeners]) {
      try {
        listener();
      } catch (err) {
        log.error("chain registry listener threw", { err });
      }
    }
  }
  /** The chain with this slug (case-folded), or null. */
  get(slug) {
    return this.bySlug.get((slug ?? "").trim().toLowerCase()) ?? null;
  }
  /** Lookup by EVM hex chain id (auto `0x`-prefixes, lowercases, drops leading zeros). */
  getByHex(chainIdHex) {
    const key = hexKey(chainIdHex ?? "");
    return key ? this.byHex.get(key) ?? null : null;
  }
  /** Lookup by CAIP-2 chain id (e.g. "eip155:1"); Base58 references are case-sensitive. */
  getByCaip2(caip2) {
    return this.byCaip2.get(normalizeCaip2(caip2 ?? "")) ?? null;
  }
  /**
   * Resolve by CAIP-2, hex, bare decimal, slug, or alias — in that order, the
   * same order as `ChainRegistry.resolve` in `commons/chains/registry.py`, so a
   * spelling resolves to the same chain on both sides of the wire. Every status
   * resolves.
   */
  resolve(value) {
    if (value == null) return null;
    const token = String(value).trim();
    if (!token) return null;
    if (token.includes(":")) {
      const hit = this.getByCaip2(token);
      if (hit) return hit;
    }
    const folded = token.toLowerCase();
    if (folded.startsWith("0x")) {
      const hit = this.getByHex(folded);
      if (hit) return hit;
    } else if (/^\d+$/.test(folded)) {
      const hit = this.byHex.get(`0x${BigInt(folded).toString(16)}`);
      if (hit) return hit;
    }
    return this.bySlug.get(folded) ?? this.byAlias.get(folded) ?? null;
  }
  /** Every registered chain, whatever its status, sorted by slug. A fresh array. */
  all() {
    this.sorted ??= Object.freeze(
      [...this.byCaip2.values()].sort((a, b) => a.slug.localeCompare(b.slug))
    );
    return [...this.sorted];
  }
  /** The chains offered for new work (status `active`), sorted by slug. */
  active() {
    return this.all().filter(isActiveChain);
  }
  byFamily(family) {
    return this.all().filter((c) => c.family === family);
  }
};
var registry = globalSingleton(
  "chains/registry.v2",
  () => new ChainRegistry(BUNDLED_CHAINS)
);
function registerChain(chain, opts) {
  registry.register(chain, opts);
}
var hydratedOnly = globalSingleton("chains/hydrated-only.v1", () => /* @__PURE__ */ new Set());
function hydrateChainRegistry(rows, { replace = true, complete = false } = {}) {
  const added = [];
  const replaced = [];
  const removed = [];
  const rejected = [];
  const incomingSlugs = /* @__PURE__ */ new Map();
  for (const row of rows) {
    if (row && typeof row === "object") {
      const { caip2, slug } = row;
      if (typeof caip2 === "string" && typeof slug === "string") incomingSlugs.set(normalizeCaip2(caip2), slug);
    }
  }
  const mainnetSlugOf = (caip2) => incomingSlugs.get(normalizeCaip2(caip2)) ?? registry.resolve(caip2)?.slug ?? null;
  registry.batch(() => {
    for (const row of rows) {
      const claimed = row && typeof row === "object" ? row.caip2 : void 0;
      const label = typeof claimed === "string" ? claimed : null;
      let chain;
      try {
        chain = chainFromWire(row, mainnetSlugOf);
      } catch (err) {
        rejected.push({ caip2: label, reason: err instanceof Error ? err.message : String(err) });
        continue;
      }
      const existing = registry.getByCaip2(chain.caip2);
      if (existing && !replace) continue;
      try {
        registry.register(chain, { replace: true });
      } catch (err) {
        rejected.push({ caip2: chain.caip2, reason: err instanceof Error ? err.message : String(err) });
        continue;
      }
      (existing ? replaced : added).push(chain.caip2);
      if (!existing) hydratedOnly.add(normalizeCaip2(chain.caip2));
    }
    if (!complete) return;
    const carried = /* @__PURE__ */ new Set();
    for (const row of rows) {
      const claimed = row && typeof row === "object" ? row.caip2 : void 0;
      if (typeof claimed === "string") carried.add(normalizeCaip2(claimed));
    }
    for (const caip2 of [...hydratedOnly]) {
      if (carried.has(caip2)) continue;
      hydratedOnly.delete(caip2);
      if (registry.unregister(caip2)) removed.push(caip2);
    }
  });
  if (rejected.length > 0) log.warn("chain registry hydration skipped rows", { rejected });
  if (removed.length > 0) log.info("chain registry hydration removed withdrawn chains", { removed });
  return { added, replaced, removed, rejected };
}
function chainRegistryVersion() {
  return registry.version;
}
function subscribeChainRegistry(listener) {
  return registry.subscribe(listener);
}
var DEFAULT_CHAIN_ID = "eip155:1";
function normalizeCaip2(value) {
  const split = value.trim().indexOf(":");
  if (split < 0) return value.trim();
  const namespace = value.trim().slice(0, split).toLowerCase();
  let reference = value.trim().slice(split + 1);
  if (["bip122", "polkadot"].includes(namespace)) reference = reference.toLowerCase();
  if (namespace === "eip155" && /^\d+$/.test(reference)) reference = BigInt(reference).toString();
  return `${namespace}:${reference}`;
}
function toCanonicalChainId(value) {
  if (!value) return null;
  const chain = registry.resolve(value);
  if (!chain) return (value.includes(":") ? normalizeCaip2(value) : value.trim().toLowerCase()) || null;
  return chain.caip2;
}
function chainsEqual(a, b) {
  const left = toCanonicalChainId(a);
  return left !== null && left === toCanonicalChainId(b);
}
var CANONICAL_CHAIN_VALUE_KIND = "caip2";
function resolveChain(value) {
  return registry.resolve(value);
}
function toHexChainId(value) {
  if (!value) return null;
  return registry.resolve(value)?.chainIdHex ?? null;
}
var toStoredChainId = toCanonicalChainId;
function toChainSlug(value) {
  if (!value) return null;
  return registry.resolve(value)?.slug ?? value;
}
function allChainIdHex() {
  return registry.all().map((c) => c.chainIdHex).filter((x) => x !== null).sort();
}
function evmCompatibleChains() {
  return new Set(registry.all().filter((c) => c.family === ChainFamily.EVM).map((c) => c.slug));
}
function getChainMetadata(chainId) {
  return registry.resolve(chainId);
}
function chainDecodeVocabulary(chainId) {
  return decodeVocabulary(chainId ? getChainMetadata(chainId)?.family : null);
}
function chainDisplayName(value) {
  return chainDisplayNameOr(value, null);
}
function chainDisplayNameOr(value, fallback) {
  if (!value) return "";
  return getChainMetadata(value)?.name ?? (fallback || value);
}
function isEVM(chainId) {
  return registry.resolve(chainId)?.family === ChainFamily.EVM;
}
var DEFAULT_EXPLORER_PATHS = {
  tx: "tx",
  address: "address",
  block: "block",
  token: "token"
};
var explorers = globalSingleton("chains/explorers", () => ({
  overrides: /* @__PURE__ */ new Map(),
  pathDefaults: {}
}));
function explorerKey(chainId) {
  if (chainId == null) return null;
  return registry.resolve(chainId)?.slug ?? (String(chainId).trim().toLowerCase() || null);
}
function configureExplorers(config) {
  if (config.paths) explorers.pathDefaults = { ...explorers.pathDefaults, ...config.paths };
  if (config.chains) {
    for (const [id, cfg] of Object.entries(config.chains)) {
      const key = explorerKey(id);
      if (!key) continue;
      const prev = explorers.overrides.get(key);
      explorers.overrides.set(key, {
        ...prev,
        ...cfg,
        paths: { ...prev?.paths, ...cfg.paths }
      });
    }
  }
}
function clearExplorerConfig() {
  explorers.overrides.clear();
  explorers.pathDefaults = {};
}
function getExplorerConfig(chainId) {
  const chain = registry.resolve(chainId);
  const key = explorerKey(chainId);
  const override = key ? explorers.overrides.get(key) : void 0;
  const raw = override?.baseUrl ?? chain?.explorerUrl;
  if (!raw) return null;
  return { name: override?.name ?? null, baseUrl: raw.replace(/\/+$/, "") };
}
function getExplorerUrl(value, chainId, type = "tx") {
  const config = getExplorerConfig(chainId);
  if (!config) return null;
  const key = explorerKey(chainId);
  const override = key ? explorers.overrides.get(key) : void 0;
  const rowPaths = registry.resolve(chainId)?.explorerPaths;
  let path = override?.paths?.[type];
  if (path === void 0 && rowPaths && type in rowPaths) {
    const rowPath = rowPaths[type];
    if (rowPath == null) return null;
    path = rowPath;
  }
  path ??= explorers.pathDefaults[type] ?? DEFAULT_EXPLORER_PATHS[type];
  return `${config.baseUrl}/${path}/${value}`;
}
function toCaip2(chainId) {
  return registry.resolve(chainId)?.caip2 ?? null;
}
function getChainBrandColor(chainId) {
  return registry.resolve(chainId)?.brandColor ?? null;
}
var NEUTRAL_CHAIN_COLOR = "#64748b";
function hexToRgba(hex, alpha) {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return hex;
  const int = parseInt(m[1], 16);
  const r = int >> 16 & 255;
  const g = int >> 8 & 255;
  const b = int & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}
var GROUP_FILL_ALPHA = 0.06;
var GROUP_BORDER_ALPHA = 0.45;
function brandGroupTint(brandColor) {
  return {
    fill: hexToRgba(brandColor, GROUP_FILL_ALPHA),
    border: hexToRgba(brandColor, GROUP_BORDER_ALPHA),
    text: brandColor
  };
}
function getChainGroupColors(chainId) {
  const chain = getChainMetadata(chainId);
  const brandColor = chain?.brandColor ?? NEUTRAL_CHAIN_COLOR;
  return {
    slug: chain?.slug ?? null,
    name: chain?.name ?? (chainId ? String(chainId) : "Unknown chain"),
    brandColor,
    ...brandGroupTint(brandColor)
  };
}
function getChainLogo(chainId) {
  return registry.resolve(chainId)?.logoUrl ?? null;
}
function resolveChainByCaip2(caip2) {
  if (!caip2) return null;
  return registry.getByCaip2(caip2);
}
var log2 = createLogger("chains:effective");
var EFFECTIVE_CHAINS_TIMEOUT_MS = 1500;
var EFFECTIVE_CHAINS_BACKGROUND_TIMEOUT_MS = 15e3;
var EFFECTIVE_CHAINS_RETRY_DELAYS_MS = [1e3, 3e3, 1e4, 3e4, 6e4];
var EFFECTIVE_CHAINS_REFRESH_MS = 10 * 6e4;
var EffectiveChainsResponseSchema = z.object({
  revision: z.string().nullable().optional(),
  published_revision: z.string().nullable().optional(),
  provider_keys: z.record(z.string(), z.string()).optional(),
  chains: z.array(z.unknown())
});
var EffectiveChainsTimeout = class extends Error {
  constructor(ms) {
    super(`effective chain registry did not answer within ${ms} ms`);
    this.name = "EffectiveChainsTimeout";
  }
};
var EffectiveChainsAborted = class extends Error {
  constructor() {
    super("effective chain registry load was cancelled");
    this.name = "EffectiveChainsAborted";
  }
};
function bodyOf(payload) {
  if (payload && typeof payload === "object" && !Array.isArray(payload) && !("chains" in payload) && "data" in payload) {
    return payload.data;
  }
  return payload;
}
async function loadEffectiveChains(options) {
  const timeoutMs = Math.max(0, options.timeoutMs ?? EFFECTIVE_CHAINS_TIMEOUT_MS);
  const controller = new AbortController();
  let timer;
  let detach;
  const failed = (error) => {
    const message = error instanceof Error ? error.message : String(error);
    if (!(error instanceof EffectiveChainsAborted)) {
      log2.warn("effective chain registry unavailable; keeping the registry as it is", { url: options.url, error: message });
    }
    return { ok: false, revision: null, publishedRevision: null, hydration: null, unchanged: false, error: message };
  };
  if (options.signal?.aborted) return failed(new EffectiveChainsAborted());
  try {
    const stop = new Promise((_, reject) => {
      timer = setTimeout(() => {
        controller.abort();
        reject(new EffectiveChainsTimeout(timeoutMs));
      }, timeoutMs);
      const signal = options.signal;
      if (signal) {
        const onAbort = () => {
          controller.abort();
          reject(new EffectiveChainsAborted());
        };
        signal.addEventListener("abort", onAbort, { once: true });
        detach = () => signal.removeEventListener("abort", onAbort);
      }
    });
    const payload = await Promise.race([
      Promise.resolve().then(() => options.fetchJson(options.url, { signal: controller.signal })),
      stop
    ]);
    if (options.signal?.aborted) return failed(new EffectiveChainsAborted());
    const parsed = EffectiveChainsResponseSchema.safeParse(bodyOf(payload));
    if (!parsed.success) return failed(new Error(`malformed effective chain registry: ${parsed.error.message}`));
    const revision = parsed.data.revision ?? null;
    const publishedRevision = parsed.data.published_revision ?? null;
    if (revision !== null && revision === options.appliedRevision) {
      return { ok: true, revision, publishedRevision, hydration: null, unchanged: true, error: null };
    }
    const hydration = hydrateChainRegistry(parsed.data.chains, { replace: true, complete: true });
    return { ok: true, revision, publishedRevision, hydration, unchanged: false, error: null };
  } catch (error) {
    return failed(error);
  } finally {
    if (timer !== void 0) clearTimeout(timer);
    detach?.();
  }
}
function startEffectiveChainsHydration(options) {
  const retryDelays = (options.retryDelaysMs ?? EFFECTIVE_CHAINS_RETRY_DELAYS_MS).map((ms) => Math.max(0, ms));
  const refreshMs = Math.max(1e3, options.refreshMs ?? EFFECTIVE_CHAINS_REFRESH_MS);
  const timeoutMs = options.timeoutMs ?? EFFECTIVE_CHAINS_BACKGROUND_TIMEOUT_MS;
  const controller = new AbortController();
  let timer;
  let appliedRevision = null;
  let loadedOnce = false;
  let failures = 0;
  let settle = () => {
  };
  const loaded = new Promise((resolve) => {
    settle = resolve;
  });
  const stop = () => {
    if (controller.signal.aborted) return;
    controller.abort();
    if (timer !== void 0) clearTimeout(timer);
    timer = void 0;
    settle(null);
  };
  if (options.signal?.aborted) stop();
  else options.signal?.addEventListener("abort", stop, { once: true });
  const ready = async () => {
    if (!options.isReady) return true;
    try {
      return await options.isReady() === true;
    } catch {
      return false;
    }
  };
  const schedule = (ms) => {
    if (controller.signal.aborted) return;
    timer = setTimeout(() => {
      timer = void 0;
      void attempt();
    }, ms);
  };
  const attempt = async () => {
    try {
      if (controller.signal.aborted) return;
      let landed = false;
      if (await ready()) {
        if (controller.signal.aborted) return;
        const result = await loadEffectiveChains({
          fetchJson: options.fetchJson,
          url: options.url,
          timeoutMs,
          signal: controller.signal,
          appliedRevision
        });
        if (controller.signal.aborted) return;
        if (result.ok) {
          landed = true;
          appliedRevision = result.revision;
          if (!loadedOnce) {
            loadedOnce = true;
            log2.info("effective chain registry loaded", {
              revision: result.revision,
              publishedRevision: result.publishedRevision,
              added: result.hydration?.added.length ?? 0,
              replaced: result.hydration?.replaced.length ?? 0,
              removed: result.hydration?.removed.length ?? 0,
              rejected: result.hydration?.rejected.length ?? 0,
              attempts: failures + 1
            });
            settle(result);
          }
        }
      }
      if (landed || loadedOnce) {
        schedule(refreshMs);
      } else {
        schedule(failures < retryDelays.length ? retryDelays[failures] : refreshMs);
        failures += 1;
      }
    } catch (error) {
      log2.warn("effective chain registry refresh failed", { error: error instanceof Error ? error.message : String(error) });
      schedule(refreshMs);
    }
  };
  schedule(0);
  return { loaded, stop };
}

// src/chains/options.ts
function valueFor(chain, kind) {
  switch (kind) {
    case "chainIdHex":
      return chain.chainIdHex;
    case "caip2":
      return chain.caip2;
    default:
      return chain.slug;
  }
}
function chainSelectOptions(config = {}) {
  const {
    valueKind = "slug",
    evmOnly = false,
    includeTestnets = false,
    sort = "name",
    fallback = "skip",
    chains,
    includeInactive = false,
    keep
  } = config;
  const source = chains ?? registry.all();
  const kept = /* @__PURE__ */ new Set();
  for (const value of keep ?? []) {
    const chain = value ? getChainMetadata(value) : null;
    if (chain) kept.add(chain.caip2);
  }
  const out = [];
  for (const chain of source) {
    const active = isActiveChain(chain);
    if (!includeInactive && !active && !kept.has(chain.caip2)) continue;
    if (evmOnly && !chainIsEvm(chain)) continue;
    if (!includeTestnets && chain.isTestnet) continue;
    let value = valueFor(chain, valueKind);
    if (value == null) {
      if (fallback === "slug") value = chain.slug;
      else continue;
    }
    out.push({ value, label: chain.name, slug: chain.slug, isTestnet: chain.isTestnet, active, caip2: chain.caip2 });
  }
  out.sort(
    (a, b) => sort === "ethFirst" && (a.slug === "eth" || b.slug === "eth") ? a.slug === "eth" ? -1 : 1 : a.label.localeCompare(b.label)
  );
  return out;
}
function chainFilterOptions(chainIds, config = {}) {
  const { countOf, noteOf, sort = "count" } = config;
  const seen = /* @__PURE__ */ new Set();
  const out = [];
  for (const id of chainIds) {
    if (!id || seen.has(id)) continue;
    seen.add(id);
    const meta = getChainMetadata(id);
    const count = countOf?.(id);
    const note = count == null ? noteOf?.(id) : void 0;
    out.push({
      value: id,
      label: meta?.name ?? id,
      slug: meta?.slug ?? id,
      ...count != null ? { count } : {},
      ...note ? { note } : {}
    });
  }
  out.sort((a, b) => {
    if (sort === "count") {
      const delta = (b.count ?? 0) - (a.count ?? 0);
      if (delta !== 0) return delta;
    }
    return a.label.localeCompare(b.label);
  });
  return out;
}
function mergeSeenChains(previous, incoming, keep = []) {
  const seen = new Set(previous);
  const added = [];
  for (const id of [...incoming, ...keep]) {
    if (!id || seen.has(id)) continue;
    seen.add(id);
    added.push(id);
  }
  return added.length === 0 ? previous : [...previous, ...added];
}

// src/chains/finality.ts
var DEFAULT_REORG_DEPTH = 12;
var FAMILY_DEFAULT_DEPTHS = {
  [ChainFamily.EVM]: 12,
  [ChainFamily.UTXO]: 6,
  [ChainFamily.SOLANA]: 32,
  [ChainFamily.COSMOS]: 1,
  [ChainFamily.HYPERCORE]: 1,
  [ChainFamily.OTHER]: DEFAULT_REORG_DEPTH
};
function familyDefaultDepth(family) {
  return FAMILY_DEFAULT_DEPTHS[family] ?? DEFAULT_REORG_DEPTH;
}
function asChain(chainOrId) {
  return typeof chainOrId === "string" ? registry.resolve(chainOrId) : chainOrId;
}
function familyFor(chainOrId) {
  return asChain(chainOrId)?.family ?? null;
}
function finalityDepthFor(chainOrId) {
  const chain = asChain(chainOrId);
  if (!chain) return DEFAULT_REORG_DEPTH;
  return chain.reorgDepth ?? familyDefaultDepth(chain.family);
}
function finalityDepthForFamily(family) {
  return familyDefaultDepth(family);
}
var DEFAULT_BLOCK_TIME_SECONDS = 12;
var FAMILY_BLOCK_TIME_SECONDS = {
  [ChainFamily.EVM]: 12,
  [ChainFamily.UTXO]: 600,
  // Bitcoin ~10 min target
  [ChainFamily.SOLANA]: 1,
  // ~400ms slots, rounded up
  [ChainFamily.COSMOS]: 6,
  [ChainFamily.HYPERCORE]: 1,
  // sub-second HyperBFT blocks, rounded up
  [ChainFamily.OTHER]: DEFAULT_BLOCK_TIME_SECONDS
};
function blockTimeSecondsFor(chainOrId) {
  const chain = asChain(chainOrId);
  if (!chain) return DEFAULT_BLOCK_TIME_SECONDS;
  return chain.blockTimeSec ?? FAMILY_BLOCK_TIME_SECONDS[chain.family] ?? DEFAULT_BLOCK_TIME_SECONDS;
}
function confirmedBlockForHead(head, chainOrId) {
  return Math.max(0, head - finalityDepthFor(chainOrId));
}
function isBlockFinalized(blockNumber, head, chainOrId) {
  return head - blockNumber >= finalityThresholdsFor(chainOrId).finalityBlocks;
}
var DEFAULT_CONFIRMATION_BLOCKS = 32;
var DEFAULT_FINALITY_BLOCKS = 256;
function finalityThresholdsFor(chainOrId) {
  const chain = asChain(chainOrId);
  const confirmationBlocks = chain ? chain.reorgDepth : DEFAULT_CONFIRMATION_BLOCKS;
  const declared = chain?.finalityBlocks ?? void 0;
  return {
    confirmationBlocks,
    finalityBlocks: Math.max(declared ?? DEFAULT_FINALITY_BLOCKS, confirmationBlocks)
  };
}
var FinalityState = {
  UNKNOWN: "unknown",
  MEMPOOL: "mempool",
  PENDING: "pending",
  OBSERVED: "observed",
  CONFIRMED: "confirmed",
  FINALIZED: "finalized",
  REORGED: "reorged"
};
var FINALITY_VALUES = new Set(Object.values(FinalityState));
function coerceFinality(value) {
  const v = String(value ?? "").toLowerCase();
  return FINALITY_VALUES.has(v) ? v : null;
}
var FINALITY_RANK = {
  [FinalityState.UNKNOWN]: -1,
  [FinalityState.MEMPOOL]: 0,
  [FinalityState.PENDING]: 0,
  [FinalityState.OBSERVED]: 0,
  [FinalityState.REORGED]: 0,
  [FinalityState.CONFIRMED]: 1,
  [FinalityState.FINALIZED]: 2
};
function finalityRank(state) {
  return FINALITY_RANK[state] ?? -1;
}

// src/chains/reorg.ts
function confirmationsFor(blockNumber, head) {
  return Math.max(0, head - blockNumber + 1);
}
function blocksUntilFinal(blockNumber, head, chainOrId) {
  const { finalityBlocks } = finalityThresholdsFor(chainOrId);
  return Math.max(0, finalityBlocks - (head - blockNumber));
}
function confirmationsUntilConfirmed(blockNumber, head, chainOrId) {
  const { confirmationBlocks } = finalityThresholdsFor(chainOrId);
  return Math.max(0, confirmationBlocks - (head - blockNumber));
}
function computeFinalityState(input) {
  if (input.reorged) return FinalityState.REORGED;
  if (input.blockNumber == null || input.head == null) return FinalityState.OBSERVED;
  const depth = input.head - input.blockNumber;
  if (depth < 0) return FinalityState.OBSERVED;
  const { confirmationBlocks, finalityBlocks } = finalityThresholdsFor(input.chainOrId);
  if (depth >= finalityBlocks) return FinalityState.FINALIZED;
  if (depth >= confirmationBlocks) return FinalityState.CONFIRMED;
  return FinalityState.OBSERVED;
}
function finalityFromAge(input) {
  const { blockTimestamp } = input;
  if (blockTimestamp == null || blockTimestamp <= 0) return FinalityState.OBSERVED;
  const blockTime = blockTimeSecondsFor(input.chainOrId);
  if (blockTime <= 0) return FinalityState.OBSERVED;
  const ageSec = (input.now ?? Date.now()) / 1e3 - blockTimestamp;
  if (ageSec <= 0) return FinalityState.OBSERVED;
  const { confirmationBlocks, finalityBlocks } = finalityThresholdsFor(input.chainOrId);
  if (ageSec >= finalityBlocks * blockTime) return FinalityState.FINALIZED;
  if (ageSec >= confirmationBlocks * blockTime) return FinalityState.CONFIRMED;
  return FinalityState.OBSERVED;
}
function resolveFinality(input) {
  const row = coerceFinality(input.rowFinality) ?? FinalityState.UNKNOWN;
  if (row === FinalityState.REORGED || input.reorged) return FinalityState.REORGED;
  const computed = computeFinalityState(input);
  if (computed === FinalityState.REORGED) return FinalityState.REORGED;
  const best = finalityRank(computed) > finalityRank(row) ? computed : row;
  const byAge = finalityFromAge({ ...input, chainOrId: input.chainOrId });
  return finalityRank(byAge) > finalityRank(best) ? byAge : best;
}
function confirmationProgress(state, input) {
  const { blockNumber, head } = input;
  if (blockNumber == null || head == null) return null;
  const thresholds = finalityThresholdsFor(input.chainOrId);
  const required = state === FinalityState.OBSERVED ? thresholds.confirmationBlocks : state === FinalityState.CONFIRMED ? thresholds.finalityBlocks : null;
  if (required == null) return null;
  return { current: Math.max(0, head - blockNumber), required };
}
function finalityProgress(input) {
  const state = resolveFinality(input);
  return { state, confirmations: confirmationProgress(state, input) };
}
var ReorgTracker = class {
  static {
    brandClass(this, "chains/ReorgTracker");
  }
  seen = /* @__PURE__ */ new Map();
  chainMap(chainId) {
    let m = this.seen.get(chainId);
    if (!m) {
      m = /* @__PURE__ */ new Map();
      this.seen.set(chainId, m);
    }
    return m;
  }
  /** Observe a block; returns a `ReorgEvent` if its hash changed at that height. */
  observe(chainId, block) {
    const m = this.chainMap(chainId);
    const prev = m.get(block.number);
    m.set(block.number, block.hash);
    if (prev && prev !== block.hash) {
      return {
        chainId,
        height: block.number,
        previousHash: prev,
        newHash: block.hash,
        reorgDepthFrom: block.number
      };
    }
    return null;
  }
  /** True if `blockNumber` falls within the replay window of a reorg at `event`. */
  isInReplayWindow(event, blockNumber, chainOrId) {
    const depth = finalityDepthFor(chainOrId);
    return blockNumber >= event.reorgDepthFrom - depth;
  }
  reset(chainId) {
    if (chainId) this.seen.delete(chainId);
    else this.seen.clear();
  }
};

// src/display/truncation.ts
var ELLIPSIS = "\u2026";
var DEFAULT_TRUNCATION = {
  hash: { head: 10, tail: 8 },
  address: { head: 6, tail: 4 }
};
var truncation = globalSingleton("display/truncation", () => ({
  active: DEFAULT_TRUNCATION
}));
function getTruncation(kind) {
  const { active } = truncation;
  return kind === "hash" ? active.hash : active.address;
}
function hexPrefixOf(value) {
  return /^0x/i.test(value) ? value.slice(0, 2) : "";
}
function truncateMiddle(value, lengths) {
  const text = value.trim();
  if (/\s/.test(text)) return value;
  const prefix = hexPrefixOf(text);
  const body = text.slice(prefix.length);
  if (body.length <= lengths.head + lengths.tail + ELLIPSIS.length) return text;
  return `${prefix}${body.slice(0, lengths.head)}${ELLIPSIS}${body.slice(-lengths.tail)}`;
}
var BASE58 = "123456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz";
var BECH32 = "qpzry9x8gf2tvdw0s3jn54khce6mua7l";
var GENERATORS = [996825010, 642813549, 513874426, 1027748829, 705979059];
var BITCOIN_ADDRESS_NETWORKS = {
  bc: { hrp: "bc", versions: [0, 5] },
  tb: { hrp: "tb", versions: [111, 196] },
  bcrt: { hrp: "bcrt", versions: [111, 196] }
};
function decodeBase58(address, alphabet = BASE58) {
  if (!address || address.length > 90) return null;
  let value = 0n;
  for (const char of address) {
    const digit = alphabet.indexOf(char);
    if (digit < 0) return null;
    value = value * 58n + BigInt(digit);
  }
  const bytes = [];
  while (value) {
    bytes.push(Number(value & 255n));
    value >>= 8n;
  }
  for (const char of address) {
    if (char !== alphabet[0]) break;
    bytes.push(0);
  }
  return Uint8Array.from(bytes.reverse());
}
var ZCASH_ADDRESS_NETWORKS = {
  zcash: { transparent: [[28, 184], [28, 189]], sapling: "zs", unified: "u" }
};
var MONERO_ADDRESS_NETWORKS = {
  monero: { 95: ["4", "8"], 106: ["4"] }
};
var BECH32_CONST = 1;
var BECH32M_CONST = 734539939;
var SAPLING_PAYLOAD_BYTES = 43;
function decodeBech32(address, maxLength = 90) {
  if (maxLength !== null && address.length > maxLength) return null;
  const lower = address.toLowerCase();
  if (address !== lower && address !== address.toUpperCase()) return null;
  const separator = lower.lastIndexOf("1");
  if (separator < 1) return null;
  const hrp = lower.slice(0, separator);
  const data = [...lower.slice(separator + 1)].map((c) => BECH32.indexOf(c));
  if (data.length < 7 || data.some((v) => v < 0)) return null;
  const expanded = [...hrp].map((c) => c.charCodeAt(0) >> 5).concat([0], [...hrp].map((c) => c.charCodeAt(0) & 31), data);
  let checksum = 1;
  for (const value of expanded) {
    const top = checksum >>> 25;
    checksum = (checksum & 33554431) << 5 ^ value;
    GENERATORS.forEach((generator, bit) => {
      if (top >>> bit & 1) checksum ^= generator;
    });
  }
  if (checksum !== BECH32_CONST && checksum !== BECH32M_CONST) return null;
  return { hrp, data: data.slice(0, -6), constant: checksum };
}
function convertedLength(data) {
  let bits = 0, accumulator = 0, length = 0;
  for (const value of data) {
    accumulator = (accumulator << 5 | value) & 4095;
    bits += 5;
    if (bits >= 8) {
      bits -= 8;
      length++;
    }
  }
  return bits < 5 && (accumulator << 8 - bits & 255) === 0 ? length : null;
}
function base58checkPayload(address) {
  const decoded = decodeBase58(address);
  if (!decoded || decoded.length < 5) return null;
  const checksum = sha256(sha256(decoded.slice(0, -4)));
  return decoded.slice(-4).every((value, index) => value === checksum[index]) ? decoded.slice(0, -4) : null;
}
function validCosmosAddress(address, hrp) {
  const decoded = decodeBech32(address);
  return decoded !== null && decoded.hrp === hrp && decoded.constant === BECH32_CONST && [20, 32].includes(convertedLength(decoded.data) ?? -1);
}
function validBitcoinAddress(address, hrp) {
  if (address.length < 14 || address.length > 90) return false;
  const formats = hrp ? [BITCOIN_ADDRESS_NETWORKS[hrp]].filter((format) => format !== void 0) : Object.values(BITCOIN_ADDRESS_NETWORKS);
  if (!formats.length) return false;
  if (/^(bc1|tb1|bcrt1)/i.test(address)) {
    const decoded = decodeBech32(address);
    if (!decoded || !formats.some((format) => format.hrp === decoded.hrp) || !decoded.data.length) return false;
    const version = decoded.data[0];
    if (version > 16 || decoded.constant !== (version === 0 ? BECH32_CONST : BECH32M_CONST)) return false;
    const length = convertedLength(decoded.data.slice(1));
    return length !== null && length >= 2 && length <= 40 && (version !== 0 || length === 20 || length === 32);
  }
  const payload = base58checkPayload(address);
  return payload !== null && payload.length === 21 && formats.some((format) => format.versions.includes(payload[0]));
}
function validZcashAddress(address, network = "zcash") {
  const format = ZCASH_ADDRESS_NETWORKS[network];
  if (!format || !address) return false;
  if (address.startsWith("t")) {
    const payload = base58checkPayload(address);
    return payload !== null && payload.length === 22 && format.transparent.some(([a, b]) => payload[0] === a && payload[1] === b);
  }
  const unified = address.toLowerCase().startsWith(`${format.unified}1`);
  const decoded = decodeBech32(address, unified ? null : 90);
  if (!decoded) return false;
  if (decoded.hrp === format.sapling && decoded.constant === BECH32_CONST) {
    return convertedLength(decoded.data) === SAPLING_PAYLOAD_BYTES;
  }
  return decoded.hrp === format.unified && decoded.constant === BECH32M_CONST;
}
function validMoneroAddress(address, network = "monero") {
  const prefixes = MONERO_ADDRESS_NETWORKS[network]?.[address.length];
  return Boolean(prefixes?.some((prefix) => address.startsWith(prefix))) && [...address].every((char) => BASE58.includes(char));
}
function validLedgerAddress(address, family) {
  const alphabet = family === "xrpl" ? "rpshnaf39wBUDNEGHJKLM4PQRST7VWXYZ2bcdeCg65jkm8oFqi1tuvAxyz" : BASE58;
  const bytes = decodeBase58(address, alphabet);
  if (!bytes || bytes.length !== 25 || bytes[0] !== (family === "xrpl" ? 0 : 65)) return false;
  const digest = sha256(sha256(bytes.slice(0, -4)));
  return bytes.slice(-4).every((value, index) => value === digest[index]);
}

// src/chains/tx.ts
var RE_HEX64 = /^(0x)?[0-9a-fA-F]{64}$/i;
var RE_SOLANA_SIG = /^[1-9A-HJ-NP-Za-km-z]{64,88}$/;
var BARE_HEX_FAMILIES = /* @__PURE__ */ new Set([
  ChainFamily.UTXO,
  ChainFamily.XRPL,
  ChainFamily.TRON,
  ChainFamily.COSMOS
]);
function normalizeTxHash(txHash, chainId) {
  const h = txHash.trim();
  const fam = chainId ? getChainMetadata(chainId)?.family ?? null : null;
  if (fam === ChainFamily.SOLANA || !RE_HEX64.test(h) && RE_SOLANA_SIG.test(h)) {
    return h;
  }
  if (RE_HEX64.test(h)) {
    const bare = h.replace(/^0x/i, "").toLowerCase();
    if (fam !== null && BARE_HEX_FAMILIES.has(fam)) return bare;
    return hasEvmIdentifiers(fam) || /^0x/i.test(h) ? `0x${bare}` : bare;
  }
  return h;
}
function isValidTxHash(txHash, chainId) {
  const h = txHash.trim();
  if (!h) return false;
  const fam = chainId ? getChainMetadata(chainId)?.family ?? null : null;
  if (fam === ChainFamily.NEAR) return h.length >= 32 && h.length <= 44 && decodeBase58(h)?.length === 32;
  if (fam === ChainFamily.SOLANA) return RE_SOLANA_SIG.test(h);
  if (fam === ChainFamily.XRPL || fam === ChainFamily.TRON || fam === ChainFamily.COSMOS) return /^[0-9a-fA-F]{64}$/.test(h);
  if (hasEvmIdentifiers(fam) || fam === ChainFamily.UTXO) return RE_HEX64.test(h);
  return RE_HEX64.test(h) || RE_SOLANA_SIG.test(h);
}
function crossChainGroupingKey(input) {
  const chain = getChainMetadata(input.chainId);
  const chainKey = chain?.slug ?? (input.chainId ?? "unknown").toString().toLowerCase();
  return `${chainKey}:${normalizeTxHash(input.txHash, input.chainId)}`;
}
function groupByTx(records, accessor) {
  const groups = /* @__PURE__ */ new Map();
  for (const record of records) {
    const key = crossChainGroupingKey(accessor(record));
    const bucket = groups.get(key);
    if (bucket) bucket.push(record);
    else groups.set(key, [record]);
  }
  return groups;
}

// src/chains/address.ts
var EVM_ZERO_ADDRESS = "0x0000000000000000000000000000000000000000";
var RE_EVM = /^0x[0-9a-fA-F]{40}$/;
var RE_BTC_BECH32 = /^(bc1|tb1|bcrt1|zs1)[0-9ac-hj-np-z]{11,87}$/i;
var RE_XRPL = /^r[1-9A-HJ-NP-Za-km-z]{24,34}$/;
var RE_TRON = /^T[1-9A-HJ-NP-Za-km-z]{33}$/;
var RE_SOLANA = /^[1-9A-HJ-NP-Za-km-z]{32,44}$/;
var RE_COSMOS_BECH32 = /^[a-z][a-z0-9]{1,10}1[0-9ac-hj-np-z]{38,58}$/;
function addressFamily(address, chainId) {
  if (chainId) {
    const fam = getChainMetadata(chainId)?.family;
    if (fam) return fam;
  }
  if (RE_EVM.test(address)) return ChainFamily.EVM;
  if (validBitcoinAddress(address) || RE_BTC_BECH32.test(address) || validZcashAddress(address) || validMoneroAddress(address)) {
    return ChainFamily.UTXO;
  }
  if (RE_XRPL.test(address) && validLedgerAddress(address, "xrpl")) return ChainFamily.XRPL;
  if (RE_TRON.test(address) && validLedgerAddress(address, "tron")) return ChainFamily.TRON;
  if (RE_SOLANA.test(address)) return ChainFamily.SOLANA;
  if (RE_COSMOS_BECH32.test(address)) return ChainFamily.COSMOS;
  return null;
}
function normalizeAddress(address, chainId) {
  const addr = address.trim();
  const fam = addressFamily(addr, chainId);
  if (hasEvmIdentifiers(fam)) {
    const hex = addr.toLowerCase();
    return hex.startsWith("0x") ? hex : `0x${hex}`;
  }
  if (fam === ChainFamily.COSMOS && (addr === addr.toLowerCase() || addr === addr.toUpperCase())) return addr.toLowerCase();
  if (fam === null && /^0x/i.test(addr)) return addr.toLowerCase();
  if (fam === ChainFamily.UTXO && RE_BTC_BECH32.test(addr) && (addr === addr.toLowerCase() || addr === addr.toUpperCase())) return addr.toLowerCase();
  return addr;
}
function isValidAddress(address, chainId) {
  const addr = address.trim();
  if (!addr) return false;
  const fam = chainId ? getChainMetadata(chainId)?.family ?? null : addressFamily(addr);
  switch (fam) {
    case ChainFamily.EVM:
    case ChainFamily.HYPERCORE:
      return RE_EVM.test(addr);
    case ChainFamily.UTXO: {
      const chain = chainId ? resolveChain(chainId) : null;
      if (!chain) return validBitcoinAddress(addr) || validZcashAddress(addr) || validMoneroAddress(addr);
      if (chain.bech32Hrp && BITCOIN_ADDRESS_NETWORKS[chain.bech32Hrp]) return validBitcoinAddress(addr, chain.bech32Hrp);
      if (ZCASH_ADDRESS_NETWORKS[chain.slug]) return validZcashAddress(addr, chain.slug);
      if (MONERO_ADDRESS_NETWORKS[chain.slug]) return validMoneroAddress(addr, chain.slug);
      return true;
    }
    case ChainFamily.XRPL:
      return RE_XRPL.test(addr) && validLedgerAddress(addr, "xrpl");
    case ChainFamily.TRON:
      return RE_TRON.test(addr) && validLedgerAddress(addr, "tron");
    case ChainFamily.NEAR:
      return addr.length >= 2 && addr.length <= 64 && /^[a-z0-9]+(?:[._-][a-z0-9]+)*$/.test(addr);
    case ChainFamily.SOLANA:
      return RE_SOLANA.test(addr) && decodeBase58(addr)?.length === 32;
    case ChainFamily.COSMOS: {
      const hrp = chainId ? resolveChain(chainId)?.bech32Hrp : null;
      return hrp ? validCosmosAddress(addr, hrp) || validCosmosAddress(addr, `${hrp}valoper`) : RE_COSMOS_BECH32.test(addr);
    }
    default:
      return RE_EVM.test(addr) || validBitcoinAddress(addr) || validZcashAddress(addr) || validMoneroAddress(addr) || RE_SOLANA.test(addr) && decodeBase58(addr)?.length === 32 || RE_COSMOS_BECH32.test(addr);
  }
}
function defaultChainsForAddress(address) {
  const family = addressFamily(address);
  if (family === null || hasEvmIdentifiers(family)) return [];
  return registry.all().filter((chain) => chain.family === family && !chain.isTestnet && isValidAddress(address, chain.slug)).map((chain) => chain.slug).sort();
}
function isEvmAddress(address) {
  return RE_EVM.test(address.trim());
}
function isZeroAddress(address) {
  return normalizeAddress(address) === EVM_ZERO_ADDRESS;
}
function addressesEqual(a, b, chainId) {
  if (!a || !b) return false;
  return normalizeAddress(a, chainId) === normalizeAddress(b, chainId);
}
function shortenAddress(address, lengths) {
  return truncateMiddle(address, { ...getTruncation("address"), ...lengths });
}
function shortenHash(hash, lengths) {
  return truncateMiddle(hash, { ...getTruncation("hash"), ...lengths });
}
function shortenIdentifier(value) {
  return truncateMiddle(value, getTruncation(truncationKindOf(value)));
}
function shortenCompositeIdentifier(value) {
  return value.split(/([:#])/).map((part) => part === ":" || part === "#" ? part : shortenIdentifier(part)).join("");
}
var LONGER_THAN_AN_ADDRESS = /^0x[0-9a-f]{41,}$/i;
function truncationKindOf(value) {
  const text = value.trim();
  return isValidTxHash(text) || LONGER_THAN_AN_ADDRESS.test(text) ? "hash" : "address";
}

// src/chains/ingest-coverage.generated.json
var ingest_coverage_generated_default = {
  ingestable_chain_ids: [
    "bip122:000000000019d6689c085ae165831e93",
    "bip122:000000000933ea01ad0ee984209779ba",
    "bip122:00040fe8ec8471911baa1db1266ea15d",
    "cosmos:cosmoshub-4",
    "cosmos:osmosis-1",
    "cosmos:thorchain-1",
    "eip155:1",
    "eip155:10",
    "eip155:100",
    "eip155:11155111",
    "eip155:11155420",
    "eip155:1135",
    "eip155:1284",
    "eip155:1285",
    "eip155:1328",
    "eip155:1329",
    "eip155:137",
    "eip155:143",
    "eip155:2020",
    "eip155:2021",
    "eip155:25",
    "eip155:33139",
    "eip155:369",
    "eip155:42161",
    "eip155:421614",
    "eip155:43113",
    "eip155:43114",
    "eip155:4663",
    "eip155:545",
    "eip155:56",
    "eip155:57073",
    "eip155:59144",
    "eip155:747",
    "eip155:80002",
    "eip155:8453",
    "eip155:84532",
    "eip155:88888",
    "eip155:97",
    "eip155:998",
    "eip155:999",
    "hyperliq:mainnet",
    "hyperliq:testnet",
    "monero:418015bb9ae982a1975da7d79277c270",
    "near:mainnet",
    "near:testnet",
    "solana:4uhcVJyU9pJkvQyS88uRDiswHXSCkY3z",
    "solana:5eykt4UsFv8P8NJdTREpY1vzqKqZKvdp",
    "solana:EtWTRABZaYq6iMfeYKouRu166VU2xqa1",
    "tron:2494104990",
    "tron:3448148188",
    "tron:728126428",
    "xrpl:0",
    "xrpl:1"
  ],
  provider_free_sources: [
    "inline"
  ],
  routes: [
    {
      chain_ids: [
        "near:mainnet",
        "near:testnet"
      ],
      family: "near",
      provider: "near_rpc",
      select: {
        provider: "near_rpc"
      },
      source_kinds: [
        "tx_hashes",
        "tx_specs"
      ]
    },
    {
      chain_ids: [
        "cosmos:cosmoshub-4",
        "cosmos:osmosis-1",
        "cosmos:thorchain-1"
      ],
      family: "cosmos",
      provider: "cosmos_lcd",
      select: {
        provider: "cosmos_lcd"
      },
      source_kinds: [
        "tx_hashes",
        "tx_specs"
      ]
    },
    {
      chain_ids: [
        "xrpl:0",
        "xrpl:1"
      ],
      family: "xrpl",
      provider: "xrpscan",
      select: {
        provider: "xrpscan"
      },
      source_kinds: [
        "latest",
        "tx_hashes",
        "tx_specs"
      ]
    },
    {
      chain_ids: [
        "tron:2494104990",
        "tron:3448148188",
        "tron:728126428"
      ],
      family: "tron",
      provider: "tronscan",
      select: {
        provider: "tronscan"
      },
      source_kinds: [
        "latest",
        "tx_hashes",
        "tx_specs"
      ]
    },
    {
      chain_ids: [
        "eip155:1",
        "eip155:10",
        "eip155:100",
        "eip155:11155111",
        "eip155:1135",
        "eip155:1284",
        "eip155:1285",
        "eip155:1328",
        "eip155:1329",
        "eip155:137",
        "eip155:143",
        "eip155:2020",
        "eip155:2021",
        "eip155:25",
        "eip155:369",
        "eip155:42161",
        "eip155:43114",
        "eip155:545",
        "eip155:56",
        "eip155:59144",
        "eip155:747",
        "eip155:80002",
        "eip155:8453",
        "eip155:84532",
        "eip155:88888",
        "eip155:97"
      ],
      family: "evm",
      provider: "moralis",
      select: {
        not: [
          {
            provider: "blockscout_v2"
          },
          {
            provider: "evm_rpc",
            values: [
              "ingest"
            ]
          }
        ],
        provider: "moralis"
      },
      source_kinds: [
        "block_range",
        "latest",
        "pair_swaps",
        "token_transfers",
        "tx_hashes",
        "tx_specs",
        "wallet_swaps"
      ]
    },
    {
      chain_ids: [
        "eip155:998",
        "eip155:999"
      ],
      family: "evm",
      provider: "evm_rpc",
      select: {
        provider: "evm_rpc",
        values: [
          "ingest"
        ]
      },
      source_kinds: [
        "tx_hashes",
        "tx_specs"
      ]
    },
    {
      chain_ids: [
        "eip155:33139",
        "eip155:4663",
        "eip155:57073"
      ],
      family: "evm",
      provider: "blockscout",
      select: {
        provider: "blockscout_v2"
      },
      source_kinds: [
        "block_range",
        "latest",
        "tx_hashes",
        "tx_specs"
      ]
    },
    {
      chain_ids: [
        "eip155:11155420",
        "eip155:421614",
        "eip155:43113"
      ],
      family: "evm",
      provider: "etherscan_family",
      select: {
        not: [
          {
            provider: "moralis"
          },
          {
            provider: "blockscout_v2"
          },
          {
            provider: "evm_rpc",
            values: [
              "ingest"
            ]
          }
        ],
        provider: "etherscan_v2"
      },
      source_kinds: [
        "tx_hashes",
        "tx_specs"
      ]
    },
    {
      chain_ids: [
        "hyperliq:mainnet",
        "hyperliq:testnet"
      ],
      family: "hypercore",
      provider: "hyperliquid",
      select: {
        provider: "hyperliquid"
      },
      source_kinds: [
        "latest",
        "tx_hashes",
        "tx_specs"
      ]
    },
    {
      chain_ids: [
        "solana:4uhcVJyU9pJkvQyS88uRDiswHXSCkY3z",
        "solana:5eykt4UsFv8P8NJdTREpY1vzqKqZKvdp",
        "solana:EtWTRABZaYq6iMfeYKouRu166VU2xqa1"
      ],
      family: "solana",
      provider: "helius",
      select: {
        provider: "helius"
      },
      source_kinds: [
        "tx_hashes",
        "tx_specs"
      ]
    },
    {
      chain_ids: [
        "bip122:000000000019d6689c085ae165831e93",
        "bip122:000000000933ea01ad0ee984209779ba"
      ],
      family: "utxo",
      provider: "esplora",
      select: {
        provider: "esplora"
      },
      source_kinds: [
        "latest",
        "tx_hashes",
        "tx_specs"
      ]
    },
    {
      chain_ids: [
        "bip122:00040fe8ec8471911baa1db1266ea15d",
        "monero:418015bb9ae982a1975da7d79277c270"
      ],
      family: "utxo",
      provider: "xmrchain+blockchair_api",
      select: {
        or: [
          {
            provider: "blockchair_api"
          }
        ],
        provider: "xmrchain"
      },
      source_kinds: [
        "tx_hashes",
        "tx_specs"
      ]
    }
  ],
  wallet_history_chain_ids: [
    "bip122:000000000019d6689c085ae165831e93",
    "bip122:000000000933ea01ad0ee984209779ba",
    "eip155:1",
    "eip155:10",
    "eip155:100",
    "eip155:11155111",
    "eip155:1135",
    "eip155:1284",
    "eip155:1285",
    "eip155:1328",
    "eip155:1329",
    "eip155:137",
    "eip155:143",
    "eip155:2020",
    "eip155:2021",
    "eip155:25",
    "eip155:33139",
    "eip155:369",
    "eip155:42161",
    "eip155:43114",
    "eip155:4663",
    "eip155:545",
    "eip155:56",
    "eip155:57073",
    "eip155:59144",
    "eip155:747",
    "eip155:80002",
    "eip155:8453",
    "eip155:84532",
    "eip155:88888",
    "eip155:97",
    "solana:4uhcVJyU9pJkvQyS88uRDiswHXSCkY3z",
    "solana:5eykt4UsFv8P8NJdTREpY1vzqKqZKvdp",
    "solana:EtWTRABZaYq6iMfeYKouRu166VU2xqa1",
    "tron:2494104990",
    "tron:3448148188",
    "tron:728126428",
    "xrpl:0",
    "xrpl:1"
  ]
};

// src/chains/coverage.ts
var BindingSchema = z.object({
  provider: z.string().min(1),
  values: z.array(z.string()).nullable().optional()
});
var SelectSchema = BindingSchema.extend({
  or: z.array(BindingSchema).optional(),
  not: z.array(BindingSchema).optional()
});
var RouteSchema = z.object({
  family: z.string().default(""),
  provider: z.string().default(""),
  source_kinds: z.array(z.string()).default([]),
  chain_ids: z.array(z.string()).default([]),
  // Absent on a snapshot written before routes carried rules, in which case
  // `chain_ids` is the whole answer.
  select: SelectSchema.nullable().optional()
});
var CoverageSchema = z.object({
  wallet_history_chain_ids: z.array(z.string()).default([]),
  ingestable_chain_ids: z.array(z.string()).default([]),
  provider_free_sources: z.array(z.string()).default([]),
  routes: z.array(RouteSchema).default([])
});
var SNAPSHOT2 = CoverageSchema.parse(ingest_coverage_generated_default);
var WALLET_HISTORY_CHAIN_IDS = Object.freeze(SNAPSHOT2.wallet_history_chain_ids);
function bindingOf(wire) {
  return Object.freeze({ provider: wire.provider, values: wire.values ? Object.freeze([...wire.values]) : null });
}
var RULES = Object.freeze(
  SNAPSHOT2.routes.map(
    (route) => Object.freeze({
      family: route.family,
      provider: route.provider,
      sourceKinds: Object.freeze([...route.source_kinds]),
      generatedChainIds: Object.freeze([...route.chain_ids]),
      select: route.select ? Object.freeze({
        anyOf: Object.freeze([bindingOf(route.select), ...(route.select.or ?? []).map(bindingOf)]),
        noneOf: Object.freeze((route.select.not ?? []).map(bindingOf))
      }) : null
    })
  )
);
function bound(binding, chain) {
  const value = chain.providers[binding.provider];
  return value !== void 0 && (binding.values === null || binding.values.includes(value));
}
function serves(rule, chain) {
  if (!rule.select) return rule.generatedChainIds.includes(chain.caip2);
  if (!isActiveChain(chain)) return false;
  if (rule.family && chain.family !== rule.family) return false;
  if (!rule.select.anyOf.some((binding) => bound(binding, chain))) return false;
  return !rule.select.noneOf.some((binding) => bound(binding, chain));
}
var derived = globalSingleton("chains/coverage", () => ({
  version: -1,
  routes: [],
  ingestable: [],
  kindsByChain: /* @__PURE__ */ new Map(),
  subscribed: false
}));
function recompute() {
  const chains = registry.all();
  const known = new Set(chains.map((chain) => chain.caip2));
  const kinds = /* @__PURE__ */ new Map();
  const routes = [];
  for (const rule of RULES) {
    const chainIds = /* @__PURE__ */ new Set();
    for (const chain of chains) if (serves(rule, chain)) chainIds.add(chain.caip2);
    if (!rule.select) {
      for (const id of rule.generatedChainIds) if (!known.has(id)) chainIds.add(id);
    }
    const sorted = [...chainIds].sort();
    for (const id of sorted) {
      const set = kinds.get(id) ?? /* @__PURE__ */ new Set();
      for (const kind of rule.sourceKinds) set.add(kind);
      kinds.set(id, set);
    }
    routes.push(Object.freeze({
      family: rule.family,
      provider: rule.provider,
      sourceKinds: rule.sourceKinds,
      chainIds: Object.freeze(sorted),
      select: rule.select
    }));
  }
  derived.routes.splice(0, derived.routes.length, ...routes);
  derived.ingestable.splice(0, derived.ingestable.length, ...[...kinds.keys()].sort());
  derived.kindsByChain = kinds;
  derived.version = registry.version;
}
function current() {
  if (!derived.subscribed) {
    derived.subscribed = true;
    registry.subscribe(() => {
      if (derived.version !== registry.version) recompute();
    });
  }
  if (derived.version !== registry.version) recompute();
  return derived;
}
var INGEST_ROUTES = current().routes;
function ingestRoutes() {
  return current().routes;
}
var PROVIDER_FREE_SOURCES = Object.freeze(
  SNAPSHOT2.provider_free_sources
);
var PROVIDER_FREE = new Set(PROVIDER_FREE_SOURCES);
var INGESTABLE_CHAIN_IDS = current().ingestable;
function ingestableChainIds() {
  return current().ingestable;
}
function canonicalOf(chainId) {
  return toCanonicalChainId(chainId) ?? chainId;
}
function kindsOf(chainId) {
  return current().kindsByChain.get(canonicalOf(chainId));
}
function ingestSourceKinds(chainId) {
  const kinds = new Set(PROVIDER_FREE_SOURCES);
  if (chainId) for (const kind of kindsOf(chainId) ?? []) kinds.add(kind);
  return [...kinds].sort();
}
function isIngestableChain(chainId, sourceKind) {
  if (!chainId) return false;
  const kinds = kindsOf(chainId);
  if (!sourceKind) return kinds !== void 0;
  return PROVIDER_FREE.has(sourceKind) || (kinds?.has(sourceKind) ?? false);
}
function uncoveredChains(chainIds, sourceKind) {
  const seen = /* @__PURE__ */ new Set();
  const out = [];
  for (const chainId of chainIds) {
    if (!chainId || isIngestableChain(chainId, sourceKind)) continue;
    const canonical = canonicalOf(chainId);
    if (seen.has(canonical)) continue;
    seen.add(canonical);
    out.push(canonical);
  }
  return out;
}

// src/auth/app-registry.ts
var APP_REGISTRY = {
  dashboard: { type: "external", subdomain: "dashboard", basePath: "/", localPort: 3e3 },
  "admin-portal": { type: "internal", subdomain: "admin-portal", basePath: "/", localPort: 3001 },
  "selfservice-portal": { type: "external", subdomain: "selfservice-portal", basePath: "/", localPort: 3002 },
  "analytics-portal": { type: "internal", subdomain: "analytics-portal", basePath: "/", localPort: 3003 },
  // Served at its own subdomain apex (`https://qa-portal.{root}/`); the CloudFront
  // distribution maps the bucket at root, so no `/qa/` mount path.
  "qa-portal": { type: "internal", subdomain: "qa-portal", basePath: "/", localPort: 3004 },
  // Pre-auth login website (separate repo: `authentication-loginpage`).
  // `preAuth` keeps it out of the applications.json drift guard.
  //
  // It is PATH-MOUNTED at `/login` on every app subdomain — there is no `login.`
  // host serving it. `login.authe.io` is Auth0's own custom domain (the
  // `/authorize` endpoint, see PROD_CUSTOM_DOMAIN in auth0-env.ts), and
  // `login.sandbox.authe.io` does not exist at all. This entry used to claim
  // `subdomain: "login"`, which sent every `external-site` login either to
  // Auth0's domain root (prd) or to an unresolvable host (sandbox), and the
  // login client has no callback registered on such a host either way.
  //
  // Defaulting to the dashboard's copy; `resolveLoginUrl` takes an `application`
  // to keep a user on their own subdomain instead.
  login: {
    type: "external",
    subdomain: "dashboard",
    basePath: "/login/",
    localPort: 3005,
    preAuth: true,
    aliasOf: "dashboard"
  },
  // First-login onboarding SPA (separate repo: `dashboard-setup-page`). Served
  // under `dashboard.authe.io/setup`, so it shares the dashboard origin + Auth0
  // client in prod (`aliasOf: "dashboard"`). Registered here purely so its local
  // dev origin (`http://localhost:3006`) is resolvable + `returnTo`-allowed when
  // running the fleet side-by-side; it is not a distinct gateway application.
  setup: { type: "external", subdomain: "dashboard", basePath: "/setup/", localPort: 3006, aliasOf: "dashboard" },
  // The PRIVATE share viewer (separate repo: `share-transactions-graph-private-page`).
  // Same shape as `setup`: a sub-site of the dashboard origin, mounted at
  // `/shared-graphs/private/`, using the dashboard's Auth0 client. Its callback
  // path is registered on the dashboard application in applications.json —
  // without that entry Auth0 rejects the redirect and the viewer never gets
  // past sign-in, which is exactly what it was doing.
  "shared-graphs-private": {
    type: "external",
    subdomain: "dashboard",
    basePath: "/shared-graphs/private/",
    localPort: 3007,
    aliasOf: "dashboard"
  }
};
function getAppEntry(application) {
  const entry = APP_REGISTRY[application];
  if (!entry) throw new Error(`Unknown application: ${application}`);
  return entry;
}
function resolveAppOrigin(application, ctx) {
  const entry = getAppEntry(application);
  return ctx.local ? `http://localhost:${entry.localPort}` : `https://${entry.subdomain}.${ctx.rootDomain}`;
}
function resolveAppBasePath(application, _ctx) {
  return getAppEntry(application).basePath;
}
function resolveAppUrl(target, ctx) {
  const origin = resolveAppOrigin(target.application, ctx);
  const basePath = resolveAppBasePath(target.application);
  const base = basePath.replace(/\/+$/, "");
  const path = target.path ?? "";
  if (!path || path === "/") return `${origin}${basePath}`;
  const rel = path ? path.startsWith("/") ? path : `/${path}` : "";
  const full = `${base}${rel}`;
  return `${origin}${full === "" ? "/" : full}`;
}

// src/chains/platform-links.ts
var PLATFORM_APP = "dashboard";
function platformTransactionRef(hash, chain) {
  const value = (hash ?? "").trim();
  if (!value) return "";
  if (value.includes(":") || !value.toLowerCase().startsWith("0x")) return value;
  const chainToken = (chain ?? "").trim();
  return chainToken ? `${chainToken}:${value}` : value;
}
function platformTransactionPath(hash, chain) {
  const ref = platformTransactionRef(hash, chain);
  return ref ? `/transactions/${encodeURIComponent(ref)}` : void 0;
}
function platformAddressPath(address, chain) {
  const value = (address ?? "").trim();
  if (!value) return void 0;
  const chainToken = (chain ?? "").trim();
  const path = `/addresses/${encodeURIComponent(value)}`;
  return chainToken ? `${path}?chain=${encodeURIComponent(chainToken)}` : path;
}
function platformTransactionUrl(hash, chain, ctx) {
  const path = platformTransactionPath(hash, chain);
  return path ? resolveAppUrl({ application: PLATFORM_APP, path }, ctx) : void 0;
}
function platformAddressUrl(address, chain, ctx) {
  const path = platformAddressPath(address, chain);
  return path ? resolveAppUrl({ application: PLATFORM_APP, path }, ctx) : void 0;
}

// src/chains/monogram.ts
var cache = globalSingleton("chains/monogram", () => ({
  version: -1,
  use: /* @__PURE__ */ new Map()
}));
function tickerUse() {
  if (cache.version === registry.version) return cache.use;
  const use = /* @__PURE__ */ new Map();
  for (const chain of registry.all()) {
    if (chain.isTestnet) continue;
    const ticker = chain.nativeToken?.trim().toUpperCase();
    if (ticker) use.set(ticker, (use.get(ticker) ?? 0) + 1);
  }
  cache.use = use;
  cache.version = registry.version;
  return use;
}
function fromName(name) {
  const firstWord = name.trim().split(/\s+/)[0] ?? "";
  const letters = firstWord.replace(/[^0-9a-z]/gi, "");
  return letters ? letters.slice(0, 3).toUpperCase() : null;
}
function isMachineSlug(chain) {
  const slug = chain.slug.trim().toLowerCase();
  if (/^[a-z0-9]+-\d+$/.test(slug)) return true;
  const [namespace, reference] = chain.caip2.trim().toLowerCase().split(":");
  return !!namespace && !!reference && /\d/.test(reference) && slug === `${namespace}-${reference}`;
}
var GENERIC_NAME_WORDS = /* @__PURE__ */ new Set([
  "mainnet",
  "testnet",
  "devnet",
  "network",
  "chain",
  "blockchain",
  "evm",
  "deprecated",
  "legacy"
]);
function fromImportedName(name) {
  const words = name.split(/[\s\-_/()[\].,:]+/).map((word) => word.replace(/[^0-9a-z]/gi, "")).filter(Boolean);
  const telling = words.filter((word) => !GENERIC_NAME_WORDS.has(word.toLowerCase()));
  const use = telling.length > 0 ? telling : words;
  const first = use[0];
  if (!first) return null;
  const initials = use.length > 1 ? use.slice(0, 3).map((word) => word[0]).join("").toUpperCase() : null;
  if (first.length === 1 && initials) return { letters: initials, misread: false, initials };
  const letters = first.slice(0, 3).toUpperCase();
  const misread = letters.length < first.length && (tickerUse().get(letters) ?? 0) > 1;
  return { letters, misread, initials };
}
function chainMonogram(chain, fallback) {
  const plain = fallback.slice(0, 3).toUpperCase();
  if (!chain) return plain;
  const ticker = chain.nativeToken?.trim().toUpperCase() ?? "";
  if (isMachineSlug(chain)) {
    const named = fromImportedName(chain.name);
    if (!named) return ticker.slice(0, 3) || plain;
    if (!named.misread) return named.letters;
    if (ticker && (tickerUse().get(ticker) ?? 0) <= 1) return ticker.slice(0, 3);
    return named.initials ?? named.letters;
  }
  if (ticker) {
    const shared = (tickerUse().get(ticker) ?? 0) > 1;
    const itsOwn = !chain.isTestnet && (chain.slug.toUpperCase() === ticker || chain.aliases.some((a) => a.toUpperCase() === ticker));
    if (!shared || itsOwn) return ticker.slice(0, 3);
  }
  return fromName(chain.name) ?? (ticker.slice(0, 3) || plain);
}

export { BUNDLED_CHAINS, CANONICAL_CHAIN_VALUE_KIND, CHAIN_PROVIDER_KEYS, ChainFamily, ChainLayer, ChainStatus, ChainWireSchema, DEFAULT_BLOCK_TIME_SECONDS, DEFAULT_CHAIN_ID, DEFAULT_CONFIRMATION_BLOCKS, DEFAULT_FINALITY_BLOCKS, DEFAULT_REORG_DEPTH, EFFECTIVE_CHAINS_BACKGROUND_TIMEOUT_MS, EFFECTIVE_CHAINS_REFRESH_MS, EFFECTIVE_CHAINS_RETRY_DELAYS_MS, EFFECTIVE_CHAINS_TIMEOUT_MS, EVM_IDENTIFIER_FAMILIES, EVM_ZERO_ADDRESS, EffectiveChainsResponseSchema, FinalityState, INGESTABLE_CHAIN_IDS, INGEST_ROUTES, NATIVE_TOKEN, PLATFORM_APP, PROTOCOL_FACT_LINKS, PROVIDER_FREE_SOURCES, ReorgTracker, WALLET_HISTORY_CHAIN_IDS, addressFamily, addressesEqual, allChainIdHex, blockTimeSecondsFor, blocksUntilFinal, brandGroupTint, chainDecodeVocabulary, chainDisplayName, chainDisplayNameOr, chainFilterOptions, chainFromWire, chainIsEvm, chainMonogram, chainRegistryVersion, chainSelectOptions, chainsEqual, clearExplorerConfig, coerceFinality, computeFinalityState, configureExplorers, confirmationProgress, confirmationsFor, confirmationsUntilConfirmed, confirmedBlockForHead, crossChainGroupingKey, decodeVocabulary, defaultChainsForAddress, evmCompatibleChains, factLinkHref, factLinkMatches, familyDefaultDepth, familyFor, finalityDepthFor, finalityDepthForFamily, finalityFromAge, finalityProgress, finalityRank, finalityThresholdsFor, foldAliases, getChainBrandColor, getChainGroupColors, getChainLogo, getChainMetadata, getExplorerConfig, getExplorerUrl, getFactLink, getFactLinkUrl, groupByTx, hasEvmIdentifiers, hydrateChainRegistry, ingestRoutes, ingestSourceKinds, ingestableChainIds, isActiveChain, isBlockFinalized, isChainFamily, isEVM, isEvmAddress, isIngestableChain, isValidAddress, isValidTxHash, isZeroAddress, loadEffectiveChains, mergeSeenChains, normalizeAddress, normalizeCaip2, normalizeTxHash, platformAddressPath, platformAddressUrl, platformTransactionPath, platformTransactionRef, platformTransactionUrl, registerChain, registry, rejectedFactLinks, resolveChain, resolveChainByCaip2, resolveFinality, shortenAddress, shortenCompositeIdentifier, shortenHash, shortenIdentifier, startEffectiveChainsHydration, subscribeChainRegistry, toCaip2, toCanonicalChainId, toChainSlug, toHexChainId, toStoredChainId, truncationKindOf, uncoveredChains };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map